import { useRef, useEffect, useCallback, useState, memo, useMemo } from 'react'
import { useMutation } from 'convex/react'
import { api } from '../../../convex/_generated/api'
import { Hourglass, Loader2, Film, Volume2, VolumeX, ChevronDown, ChevronUp, ChevronRight, Music, Zap, Mic, ZoomIn, ZoomOut, FileText, ImageIcon, UserRound, ArrowRight, ArrowUpFromLine, FileEdit, Video, Sparkles, Clapperboard, Gauge, Type, Play, Pause, SkipBack, SkipForward, X, Scissors } from 'lucide-react'
import { useVideoSeekOptional } from '@/contexts/video-seek-context'
import { useAuth } from '@/contexts/auth-context'
import { resolveDisplayClipMode, hasClipContinuity } from './clipMode'
import { getEffectiveDuration, buildTimelineModel, type TimelineClip, type TimelineMode } from '@/lib/clipUtils'

const videoThumbCache = new Map<string, string>()
const videoThumbPending = new Set<string>()

// Throttle thumbnail generation — limit concurrent video loads to avoid
// flooding the network during playback
const THUMB_CONCURRENCY = 3
let thumbsLoading = 0
const thumbLoadQueue: Array<{ url: string; onDone: (url: string, dataUrl: string) => void }> = []

function processThumbQueue() {
  while (thumbsLoading < THUMB_CONCURRENCY && thumbLoadQueue.length > 0) {
    const { url, onDone } = thumbLoadQueue.shift()!
    thumbsLoading++
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.muted = true
    video.preload = 'auto'
    video.src = url
    video.currentTime = 0.5
    const finish = () => {
      video.src = ''
      video.load()
      thumbsLoading--
      processThumbQueue()
    }
    video.addEventListener('seeked', () => {
      try {
        const canvas = document.createElement('canvas')
        canvas.width = 128
        canvas.height = 72
        const ctx = canvas.getContext('2d')
        if (ctx) {
          ctx.drawImage(video, 0, 0, 128, 72)
          const dataUrl = canvas.toDataURL('image/jpeg', 0.7)
          videoThumbCache.set(url, dataUrl)
          videoThumbPending.delete(url)
          onDone(url, dataUrl)
        }
      } catch {
        videoThumbPending.delete(url)
      }
      finish()
    }, { once: true })
    video.addEventListener('error', () => {
      videoThumbPending.delete(url)
      finish()
    }, { once: true })
  }
}

function linkGroupColor(id: string): string {
  let hash = 0;
  for (let i = 0; i < id.length; i++) hash = ((hash << 5) - hash + id.charCodeAt(i)) | 0;
  return `hsl(${Math.abs(hash) % 360}, 65%, 55%)`;
}

function useVideoThumbnails(videoUrls: string[]): Map<string, string> {
  const [thumbs, setThumbs] = useState<Map<string, string>>(() => {
    const init = new Map<string, string>()
    for (const url of videoUrls) {
      const cached = videoThumbCache.get(url)
      if (cached) init.set(url, cached)
    }
    return init
  })

  useEffect(() => {
    for (const url of videoUrls) {
      if (videoThumbCache.has(url) || videoThumbPending.has(url)) continue
      videoThumbPending.add(url)
      thumbLoadQueue.push({
        url,
        onDone: (u, dataUrl) => setThumbs(prev => new Map(prev).set(u, dataUrl)),
      })
    }
    processThumbQueue()
  }, [videoUrls])

  return thumbs
}

interface Keyframe {
  url: string
  timestamp: number
  description: string
  status?: 'pending' | 'generating' | 'ready' | 'failed'
}

interface DialogLine {
  characterId: string
  characterName: string
  text: string
  tone?: string
}

interface WorkflowScene {
  _id: string
  title?: string
  description?: string
  videoPrompt?: string
  duration: number
  storyboardImageUrl?: string
  videoUrl?: string
  firstFrameUrl?: string
  lastFrameUrl?: string
  referenceFrameUrl?: string
  startKeyframeUrl?: string
  endKeyframeUrl?: string
  isJumpCut?: boolean
  videoStatus: 'pending' | 'generating_image' | 'image_ready' | 'generating_video' | 'completed' | 'failed' | 'cancelled'
  keyframes?: Keyframe[]
  keyframeStatus?: 'none' | 'generating' | 'ready' | 'failed'
  staleReasons?: string[]
  dialog?: DialogLine[]
  nativeAudioEnabled?: boolean
  transcription?: {
    words?: { w: string; s: number; e: number }[]
    text: string
    language?: string
  }
  transcriptionStatus?: string
  solidColor?: string
  generationMode?: string
  linkGroupId?: string
  startKeyframeSource?: string
  trimStart?: number
  trimEnd?: number
  speed?: number
  timelineStartTime?: number
  transition?: { type: string; duration: number }
  effects?: {
    fadeIn?: { color: string; duration: number }
    fadeOut?: { color: string; duration: number }
    colorGrade?: string
    letterbox?: boolean
    vignette?: number
    grain?: number
    blur?: number
    bloom?: number
    chromaticAberration?: number
    vhs?: boolean
    shake?: number
    kenBurns?: { startScale: number; endScale: number; startX?: number; startY?: number; endX?: number; endY?: number }
  }
}

interface BeatData {
  beats: number[]
  hits: Array<{ time: number; strength: number; score: number }>
  strongHits: number[]
  downbeats: number[]
  bpm: number
}

interface AudioTrack {
  id: string
  type: 'music' | 'sfx' | 'voiceover'
  name: string
  url: string
  startTime: number
  duration: number
  trimStart?: number
  volume: number
  fadeIn?: number
  fadeOut?: number
  status: 'pending' | 'generating' | 'ready' | 'failed'
  transcriptionWords?: Array<{ w: string; s: number; e: number }>
  volumeEnvelope?: Array<{ time: number; volume: number }>
  metadata?: { beatData?: BeatData; [key: string]: unknown }
}

interface TextOverlay {
  id: string
  text: string
  startTime: number
  duration: number
  position?: string
  style?: string
  color?: string
}

interface TimelineProps {
  scenes: WorkflowScene[]
  audioTracks: AudioTrack[]
  textOverlays?: TextOverlay[]
  currentTime: number
  totalDuration: number
  onSeek: (time: number) => void
  onScrubStart?: () => void
  onScrub?: (time: number) => void
  onScrubEnd?: () => void
  currentSceneIndex?: number
  onSceneChange?: (index: number) => void
  onAudioTrackUpdate?: (trackId: string, startTime: number, duration: number, trimStart?: number) => void
  onTextOverlayUpdate?: (overlayId: string, startTime: number, duration: number) => void
  onPlayPause?: () => void
  onDeleteClips?: (clipIds: string[]) => void
  onDeleteAudioTracks?: (trackIds: string[]) => void
  onClipReorder?: (clipOrder: string[]) => void
  onClipTrimUpdate?: (clipId: string, trimStart: number, trimEnd: number) => void
  onUndo?: () => void
  onRedo?: () => void
  isExpanded?: boolean
  onToggleExpand?: () => void
  isPlaying?: boolean
  workflowId?: string
  onTransitionClick?: (clipId: string) => void
  onRemoveTextOverlay?: (overlayId: string) => void
  selectedOverlayId?: string | null
  onOverlaySelect?: (overlayId: string | null) => void
  exportDuration?: number
  onExportDurationChange?: (duration: number | undefined) => void
  onEnvelopeChange?: (trackId: string, envelope: Array<{ time: number; volume: number }>) => void
  timelineMode?: TimelineMode
  /**
   * When true, the initial pixels-per-second is computed so the full timeline
   * fits the container width on first render and re-fits if the container
   * resizes. Useful when embedding Timeline in a narrow side pane where the
   * default 15.5 pps overflows.
   */
  autoFitWidth?: boolean
}

const DEFAULT_PPS = 15.5
const MIN_PPS = 2
const MAX_PPS = 200
const VIDEO_TRACK_HEIGHT = 64
const AUDIO_TRACK_HEIGHT = 48
const VIDEO_AUDIO_TRACK_HEIGHT = 36
const TIME_RULER_HEIGHT = 22
const TRACK_LABEL_WIDTH = 100
const SNAP_THRESHOLD = 8
const DRAG_START_THRESHOLD = 5
type DebugRange = { start: number; end: number }

interface ClipDragState {
  type: 'reorder' | 'trim-left' | 'trim-right'
  clipId: string
  clipIndex: number
  initialX: number
  initialLeft: number
  initialWidth: number
  initialTrimStart: number
  initialTrimEnd: number
  currentX: number
  targetIndex: number
  snappedTo: number | null
  started: boolean
}

interface SceneBoundary {
  start: number
  end: number
  scene: WorkflowScene
  index: number
}

function getSceneBoundaries(scenes: WorkflowScene[], timelineMode?: TimelineMode): SceneBoundary[] {
  const model = buildTimelineModel(scenes as unknown as TimelineClip[], { mode: timelineMode })
  return model.entries.map(e => ({
    start: e.globalStart,
    end: e.globalEnd,
    scene: e.clip as unknown as WorkflowScene,
    index: e.clipIndex,
  }))
}

function getTimeMarkers(totalDuration: number, pps: number): number[] {
  const markers: number[] = []
  const minPixelGap = 60
  const rawInterval = minPixelGap / pps
  const niceIntervals = [1, 2, 5, 10, 15, 30, 60]
  const interval = niceIntervals.find(i => i >= rawInterval) || Math.ceil(rawInterval / 10) * 10
  let time = interval
  while (time < totalDuration) {
    markers.push(time)
    time += interval
  }
  return markers
}

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

function GenerationBadge({ scene, hasContinuity }: { scene: WorkflowScene; hasContinuity?: boolean }) {
  const base = "inline-flex items-center justify-center w-4 h-4 rounded-sm"
  if (scene.videoStatus === 'generating_video' || scene.videoStatus === 'generating_image') {
    return <span className={`${base} bg-blue-700 text-white`} title="Generating video"><Loader2 size={8} className="animate-spin" /></span>
  }
  if (scene.videoStatus === 'completed') {
    const mode = resolveDisplayClipMode(scene.generationMode, !!hasContinuity, scene.startKeyframeSource)
    if (mode === 'continuation') {
      return (
        <span className={`${base} bg-teal-600/85 text-white`} title="Continuation from previous clip">
          <ArrowRight size={8} />
        </span>
      )
    }
    const modeLabels: Record<string, { bg: string; icon: React.ReactNode; title: string }> = {
      'text-to-video': { bg: 'bg-neutral-600/85', icon: <FileText size={8} />, title: 'Text to Video' },
      'generated-keyframe': { bg: 'bg-indigo-600/85', icon: <Sparkles size={8} />, title: 'Generated Keyframe' },
      'image-to-video': { bg: 'bg-blue-600/85', icon: <ImageIcon size={8} />, title: 'Image to Video' },
      'reference-to-video': { bg: 'bg-emerald-600/85', icon: <UserRound size={8} />, title: 'Character Reference' },
      'first-last-frame': { bg: 'bg-purple-600/85', icon: <ArrowRight size={8} />, title: 'Keyframe Transition' },
      'edit-video': { bg: 'bg-amber-600/85', icon: <FileEdit size={8} />, title: 'Edit Video' },
      'video-reference': { bg: 'bg-teal-700/85', icon: <Video size={8} />, title: 'Video Style Reference' },
      'avatar': { bg: 'bg-violet-600/85', icon: <Mic size={8} />, title: 'Avatar' },
      'multi-shot': { bg: 'bg-cyan-600/85', icon: <Clapperboard size={8} />, title: 'Multi-Shot' },
      'uploaded': { bg: 'bg-stone-600/85', icon: <ArrowUpFromLine size={8} />, title: 'Uploaded' },
    }
    const cfg = mode ? modeLabels[mode] : null
    if (cfg) return <span className={`${base} ${cfg.bg} text-white`} title={cfg.title}>{cfg.icon}</span>
  }
  return null
}

const waveformCache = new Map<string, number[]>()
const waveformPending = new Map<string, Promise<number[]>>()

function getWaveformCacheKey(url: string, numBuckets: number): string {
  return `${url}::${numBuckets}`
}

function useWaveformData(url: string | undefined, numBuckets: number = 250): number[] | null {
  const cacheKey = url ? getWaveformCacheKey(url, numBuckets) : null
  const [data, setData] = useState<number[] | null>(() => cacheKey ? waveformCache.get(cacheKey) || null : null)

  useEffect(() => {
    if (!url || !cacheKey) { setData(null); return }
    if (waveformCache.has(cacheKey)) { setData(waveformCache.get(cacheKey)!); return }

    let cancelled = false
    const loadWaveform = () => {
      if (waveformPending.has(cacheKey)) return waveformPending.get(cacheKey)!
      const pending = fetch(url)
        .catch(() => fetch(url, { cache: "reload" }))
        .then(r => r.arrayBuffer())
        .then(buf => {
          const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)()
          return audioCtx.decodeAudioData(buf).finally(() => audioCtx.close())
        })
        .then(audioBuf => {
          const channel = audioBuf.getChannelData(0)
          const bucketSize = Math.max(1, Math.floor(channel.length / numBuckets))
          const amplitudes: number[] = []
          for (let i = 0; i < numBuckets; i++) {
            let sum = 0
            const start = i * bucketSize
            const end = Math.min(start + bucketSize, channel.length)
            if (start >= end) {
              amplitudes.push(0)
              continue
            }
            for (let j = start; j < end; j++) sum += Math.abs(channel[j])
            amplitudes.push(sum / (end - start))
          }
          // Normalize per-clip so the waveform shows actual dynamics.
          // Fixed reference (0.25) was clipping loud music to a flat block.
          const peak = Math.max(...amplitudes)
          // Videos without audio have near-silent tracks; treat as empty.
          if (peak < 0.005) return []
          const normalized = amplitudes.map(a => a / peak)
          waveformCache.set(cacheKey, normalized)
          return normalized
        })
        .finally(() => {
          waveformPending.delete(cacheKey)
        })
      waveformPending.set(cacheKey, pending)
      return pending
    }

    let timeoutId: ReturnType<typeof setTimeout> | null = null
    let idleId: number | null = null
    const startLoad = () => {
      loadWaveform()
        .then((normalized) => {
          if (cancelled) return
          setData(normalized)
        })
        .catch(() => { if (!cancelled) setData(null) })
    }

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(() => startLoad(), { timeout: 1200 })
    } else {
      timeoutId = setTimeout(startLoad, 250)
    }

    return () => {
      cancelled = true
      if (timeoutId !== null) clearTimeout(timeoutId)
      if (idleId !== null && typeof window.cancelIdleCallback === 'function') {
        window.cancelIdleCallback(idleId)
      }
    }
  }, [url, numBuckets, cacheKey])

  return data
}

const WAVEFORM_COLOR = { r: 160, g: 100, b: 240 }

const WaveformCanvas = memo(function WaveformCanvas({ data, color: customColor, alpha, gain = 1 }: { data: number[]; color?: { r: number; g: number; b: number }; alpha?: number; gain?: number }) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const draw = useCallback(() => {
    const canvas = canvasRef.current
    const container = containerRef.current
    if (!canvas || !container || !data.length) return
    const { width: w, height: h } = container.getBoundingClientRect()
    if (w === 0 || h === 0) return
    const dpr = window.devicePixelRatio || 1
    canvas.width = w * dpr
    canvas.height = h * dpr
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.scale(dpr, dpr)
    ctx.clearRect(0, 0, w, h)
    const { r, g, b } = customColor || WAVEFORM_COLOR
    const a = alpha ?? 0.65
    const amp = gain * 0.9

    const step = w / data.length
    ctx.beginPath()
    ctx.moveTo(0, h)
    for (let i = 0; i < data.length; i++) {
      const x = (i + 0.5) * step
      const y = h - data[i] * h * amp
      if (i === 0) { ctx.lineTo(x, y); continue }
      const px = (i - 0.5) * step
      const blend = 0.3
      const cx = px + (x - px) * blend
      const cx2 = px + (x - px) * (1 - blend)
      ctx.bezierCurveTo(cx, h - data[i - 1] * h * amp, cx2, y, x, y)
    }
    ctx.lineTo(w, h)
    ctx.closePath()
    ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a})`
    ctx.fill()
  }, [data, customColor, alpha, gain])

  useEffect(() => {
    draw()
    const container = containerRef.current
    if (!container) return
    const ro = new ResizeObserver(() => draw())
    ro.observe(container)
    return () => ro.disconnect()
  }, [draw])

  return (
    <div ref={containerRef} className="w-full h-full">
      <canvas ref={canvasRef} className="w-full h-full" />
    </div>
  )
})

function getSceneAudioSource(scene: WorkflowScene): string | undefined {
  return scene.videoUrl
}

const VideoAudioBlock = memo(function VideoAudioBlock({ scene, numBuckets, gain = 1 }: {
  scene: WorkflowScene; numBuckets: number; gain?: number
}) {
  const data = useWaveformData(getSceneAudioSource(scene), numBuckets)
  if (!data || data.length === 0) return null
  return <WaveformCanvas data={data} alpha={0.8} gain={gain} />
})

function GainLine({ active }: { active: boolean }) {
  return <div className={`h-px transition-[height] duration-75 ${active ? 'h-0.5 bg-surface-accent shadow-[0_0_4px_rgba(251,191,36,0.5)]' : 'bg-surface-accent/70 group-hover/gain:h-0.5'}`} />
}

const GainClip = memo(function GainClip({ scene, numBuckets, volume, muted, left, width, transition, activeClipId, onToggleMute, currentTime, globalStart, onSeek }: {
  scene: WorkflowScene; numBuckets: number; volume: number; muted: boolean
  left: number; width: number; transition: string
  activeClipId: string | null; onToggleMute: () => void
  currentTime?: number; globalStart?: number; onSeek?: (time: number) => void
}) {
  const linePos = Math.sqrt(volume)
  const gainLineY = `${(1 - linePos) * 100}%`
  const volPercent = Math.round(volume * 100)
  const isActive = activeClipId === scene._id
  const words = scene.transcription?.words

  return (
    <div
      className={`absolute top-0 bottom-0 overflow-hidden bg-surface-elevated/60 group/gain ${muted ? 'opacity-30' : ''}`}
      style={{ left, width, transition }}
      onDoubleClick={(e) => { e.stopPropagation(); onToggleMute() }}
      title={muted ? 'Double-click to unmute' : `${volPercent}%`}
    >
      <VideoAudioBlock scene={scene} numBuckets={numBuckets} gain={muted ? 0 : volume} />
      {words && words.length > 0 && currentTime !== undefined && globalStart !== undefined && (
        <InlineTranscriptWords
          words={words}
          widthPx={width}
          currentTime={currentTime}
          trimStart={scene.trimStart || 0}
          trimEnd={scene.trimEnd || 0}
          speed={scene.speed || 1}
          clipDuration={scene.duration}
          globalStart={globalStart}
          onSeek={onSeek}
        />
      )}
      <div className="absolute left-0 right-0 pointer-events-none" style={{ top: gainLineY }}>
        <GainLine active={isActive} />
      </div>
      {isActive && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/70 text-surface-accent text-[9px] font-mono px-1.5 py-0.5 rounded pointer-events-none z-10">
          {volPercent}%
        </div>
      )}
    </div>
  )
})

const TEXT_OVERLAY_TRACK_HEIGHT = 28
const TOOLBAR_HEIGHT = 36
const COLLAPSE_BAR_HEIGHT = 24

export function getTimelineHeight(opts: {
  hasVideoAudio: boolean
  musicCount: number
  sfxCount: number
  voiceCount: number
  textOverlayCount: number
}): number {
  let h = TOOLBAR_HEIGHT + TIME_RULER_HEIGHT + VIDEO_TRACK_HEIGHT
  if (opts.hasVideoAudio) h += VIDEO_AUDIO_TRACK_HEIGHT
  if (opts.musicCount > 0) h += AUDIO_TRACK_HEIGHT
  if (opts.sfxCount > 0) h += AUDIO_TRACK_HEIGHT
  if (opts.voiceCount > 0) h += AUDIO_TRACK_HEIGHT
  if (opts.textOverlayCount > 0) h += TEXT_OVERLAY_TRACK_HEIGHT
  h += COLLAPSE_BAR_HEIGHT
  return h
}

export function Timeline({
  scenes,
  audioTracks,
  textOverlays = [],
  currentTime,
  totalDuration,
  onSeek,
  onScrubStart,
  onScrub,
  onScrubEnd,
  currentSceneIndex,
  onSceneChange,
  onAudioTrackUpdate,
  onTextOverlayUpdate,
  onPlayPause,
  onDeleteClips,
  onDeleteAudioTracks,
  onClipReorder,
  onClipTrimUpdate,
  onUndo,
  onRedo,
  isExpanded = true,
  onToggleExpand,
  isPlaying = false,
  workflowId,
  onTransitionClick,
  onRemoveTextOverlay,
  selectedOverlayId,
  onOverlaySelect,
  exportDuration,
  onExportDurationChange,
  onEnvelopeChange,
  timelineMode,
  autoFitWidth = false,
}: TimelineProps) {
  const { user } = useAuth()
  const isAdmin = user?.isAdmin === true
  const setTimelineModeMutation = useMutation(api.workflows.setTimelineMode)
  const splitClipMutation = useMutation(api.workflows.splitClip)

  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const timelineRef = useRef<HTMLDivElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const committedTimeRef = useRef(currentTime)
  const isHoveringRef = useRef(false)
  const playheadRef = useRef<HTMLDivElement>(null)
  const [collapsedTracks, setCollapsedTracks] = useState<Record<string, boolean>>({})
  const [pixelsPerSecond, setPixelsPerSecond] = useState(() => {
    if (workflowId) {
      const saved = localStorage.getItem(`timeline-zoom:${workflowId}`)
      if (saved) {
        const v = parseFloat(saved)
        if (v >= MIN_PPS && v <= MAX_PPS) return v
      }
    }
    return DEFAULT_PPS
  })
  useEffect(() => {
    if (workflowId) localStorage.setItem(`timeline-zoom:${workflowId}`, String(pixelsPerSecond))
  }, [pixelsPerSecond, workflowId])

  // Auto-fit zoom: shrink pps so timelineWidth fits the visible track area.
  // Re-fits on container resize. Only active when autoFitWidth is set.
  useEffect(() => {
    if (!autoFitWidth) return
    const el = containerRef.current
    if (!el) return
    const fit = () => {
      const w = el.clientWidth
      if (w <= 0 || totalDuration <= 0) return
      // Track area = container width - label column - small breathing room
      const trackWidth = Math.max(40, w - TRACK_LABEL_WIDTH - 12)
      const targetPps = Math.max(MIN_PPS, Math.min(MAX_PPS, trackWidth / totalDuration))
      setPixelsPerSecond(targetPps)
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(el)
    return () => ro.disconnect()
  }, [autoFitWidth, totalDuration])

  const [selectedItems, setSelectedItems] = useState<Set<string>>(new Set())
  const [clipDrag, setClipDrag] = useState<ClipDragState | null>(null)
  const clipDragRef = useRef<ClipDragState | null>(null)
  const [gainDragClipId, setGainDragClipId] = useState<string | null>(null)
  const gainDragRef = useRef(false)
  const [pendingReorder, setPendingReorder] = useState<{ dragIndex: number; targetIndex: number; ghostLeft: number; settled: boolean } | null>(null)
  const suppressTransitionRef = useRef(false)
  const [suppressTransition, setSuppressTransition] = useState(false)
  const prevPendingRef = useRef<typeof pendingReorder>(null)
  const [committedClipTrim, setCommittedClipTrim] = useState<{ clipId: string; trimStart: number; trimEnd: number } | null>(null)
  const [textOverlayDrag, setTextOverlayDrag] = useState<{ type: 'move' | 'resize-left' | 'resize-right'; overlayId: string; initialX: number; initialStartTime: number; initialDuration: number } | null>(null)
  const textOverlayLocalRef = useRef<{ startTime: number; duration: number }>({ startTime: 0, duration: 0 })
  const [textOverlayLocal, setTextOverlayLocal] = useState<{ startTime: number; duration: number } | null>(null)
  const textOverlayCommittedRef = useRef<{ overlayId: string; startTime: number; duration: number } | null>(null)
  const textOverlayDidDrag = useRef(false)
  const [showBufferDebug, setShowBufferDebug] = useState(false)
  const [bufferDebugTick, setBufferDebugTick] = useState(0)
  const [exportMarkerDrag, setExportMarkerDrag] = useState<{ initialX: number; initialDuration: number } | null>(null)
  const exportMarkerDragRef = useRef<{ initialX: number; initialDuration: number } | null>(null)
  const [exportDurationLocal, setExportDurationLocal] = useState<number | null>(null)
  const [committedExportDuration, setCommittedExportDuration] = useState<number | undefined>(undefined)
  const [isChangingTimelineMode, setIsChangingTimelineMode] = useState(false)
  const sceneBoundaries = getSceneBoundaries(scenes, timelineMode)
  const activeTimelineMode = timelineMode ?? 'ripple'
  const videoThumbs = useVideoThumbnails([])
  const sceneIdKey = useMemo(() => scenes.map(s => s._id).join(','), [scenes])
  useEffect(() => {
    if (pendingReorder && !pendingReorder.settled) {
      requestAnimationFrame(() => {
        setPendingReorder(prev => prev ? { ...prev, settled: true } : null)
      })
    }
  }, [pendingReorder])

  const handleTimelineModeChange = useCallback(async (nextMode: TimelineMode) => {
    if (!workflowId || nextMode === activeTimelineMode || isChangingTimelineMode) return
    setIsChangingTimelineMode(true)
    try {
      await setTimelineModeMutation({
        workflowId: workflowId as any,
        mode: nextMode,
      })
    } finally {
      setIsChangingTimelineMode(false)
    }
  }, [workflowId, activeTimelineMode, isChangingTimelineMode, setTimelineModeMutation])
  useEffect(() => {
    if (pendingReorder) setPendingReorder(null)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sceneIdKey])
  useEffect(() => {
    const wasPending = prevPendingRef.current
    prevPendingRef.current = pendingReorder
    if (wasPending && !pendingReorder) {
      suppressTransitionRef.current = true
      setSuppressTransition(true)
      requestAnimationFrame(() => {
        suppressTransitionRef.current = false
        setSuppressTransition(false)
      })
    }
  }, [pendingReorder])
  const seekContext = useVideoSeekOptional()
  const mutedTracks = seekContext?.audioMix.mutedTracks ?? {}
  const clipMutes = seekContext?.audioMix.clipMutes ?? {}
  const clipVolumes = seekContext?.audioMix.clipVolumes ?? {}

  useEffect(() => {
    if (!isAdmin) {
      setShowBufferDebug(false)
      return
    }
    setShowBufferDebug(true)
  }, [isAdmin])

  const toggleBufferDebug = useCallback(() => {
    if (!isAdmin) return
    setShowBufferDebug((prev) => !prev)
  }, [isAdmin])

  useEffect(() => {
    if (!isAdmin || !showBufferDebug) return
    const timer = setInterval(() => {
      setBufferDebugTick((x) => x + 1)
    }, 300)
    return () => clearInterval(timer)
  }, [isAdmin, showBufferDebug])

  const selectItem = useCallback((key: string, e: React.MouseEvent) => {
    e.stopPropagation()
    setSelectedItems(prev => {
      if (e.metaKey || e.ctrlKey) {
        const next = new Set(prev)
        if (next.has(key)) next.delete(key)
        else next.add(key)
        return next
      }
      if (prev.size === 1 && prev.has(key)) return prev
      return new Set([key])
    })
  }, [])

  const clearSelection = useCallback(() => setSelectedItems(new Set()), [])

  const [scrollRatio, setScrollRatio] = useState(0)
  const [scrollThumbRatio, setScrollThumbRatio] = useState(1)
  const scrollTrackRef = useRef<HTMLDivElement>(null)
  const hasVideoAudio = scenes.some(s => s.videoStatus === 'completed' && (s.nativeAudioEnabled || (s.transcription?.words && s.transcription.words.length > 0)))
  const maxAudioEnd = audioTracks.length > 0
    ? Math.max(...audioTracks.map(t => t.startTime + t.duration))
    : 0
  const effectiveDuration = Math.max(totalDuration, maxAudioEnd, 1)

  const clampPPS = useCallback((v: number) => Math.max(MIN_PPS, Math.min(MAX_PPS, v)), [])

  const applyZoom = useCallback((direction: 'in' | 'out', anchorClientX?: number) => {
    setPixelsPerSecond((prev) => {
      const next = clampPPS(direction === 'in' ? prev * 1.15 : prev / 1.15)
      if (Math.abs(next - prev) < 0.001) return prev

      if (anchorClientX !== undefined) {
        const container = scrollContainerRef.current
        if (container) {
          const rect = container.getBoundingClientRect()
          const anchorOffset = anchorClientX - rect.left
          const anchorTime = (container.scrollLeft + anchorOffset) / prev
          const nextScrollLeft = anchorTime * next - anchorOffset
          requestAnimationFrame(() => {
            const active = scrollContainerRef.current
            if (!active) return
            const maxScroll = Math.max(0, active.scrollWidth - active.clientWidth)
            active.scrollLeft = Math.max(0, Math.min(maxScroll, nextScrollLeft))
          })
        }
      }

      return next
    })
  }, [clampPPS])

  const zoomIn = useCallback((anchorClientX?: number) => applyZoom('in', anchorClientX), [applyZoom])
  const zoomOut = useCallback((anchorClientX?: number) => applyZoom('out', anchorClientX), [applyZoom])

  const setZoomFromSlider = useCallback((pps: number) => {
    setPixelsPerSecond(clampPPS(pps))
  }, [clampPPS])

  const updateScrollState = useCallback(() => {
    const container = scrollContainerRef.current
    if (!container) return
    const maxScroll = container.scrollWidth - container.clientWidth
    setScrollRatio(maxScroll > 0 ? container.scrollLeft / maxScroll : 0)
    setScrollThumbRatio(Math.min(1, container.clientWidth / container.scrollWidth))
  }, [])

  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return
    const handleWheel = (e: WheelEvent) => {
      if (Math.abs(e.deltaY) <= Math.abs(e.deltaX)) return
      e.preventDefault()
      if (e.deltaY < 0) zoomIn(e.clientX)
      else zoomOut(e.clientX)
    }
    container.addEventListener('wheel', handleWheel, { passive: false })
    container.addEventListener('scroll', updateScrollState)
    updateScrollState()
    return () => {
      container.removeEventListener('wheel', handleWheel)
      container.removeEventListener('scroll', updateScrollState)
    }
  }, [zoomIn, zoomOut, updateScrollState])

  const handleScrollbarMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    const track = scrollTrackRef.current
    const container = scrollContainerRef.current
    if (!track || !container) return
    const trackRect = track.getBoundingClientRect()
    const thumbWidth = trackRect.width * scrollThumbRatio
    const maxThumbLeft = trackRect.width - thumbWidth
    const clickX = e.clientX - trackRect.left - thumbWidth / 2
    const ratio = Math.max(0, Math.min(1, clickX / maxThumbLeft))
    const maxScroll = container.scrollWidth - container.clientWidth
    container.scrollLeft = ratio * maxScroll

    const handleMove = (ev: MouseEvent) => {
      const moveX = ev.clientX - trackRect.left - thumbWidth / 2
      const moveRatio = Math.max(0, Math.min(1, moveX / maxThumbLeft))
      container.scrollLeft = moveRatio * maxScroll
    }
    const handleUp = () => {
      document.removeEventListener('mousemove', handleMove)
      document.removeEventListener('mouseup', handleUp)
    }
    document.addEventListener('mousemove', handleMove)
    document.addEventListener('mouseup', handleUp)
  }, [scrollThumbRatio])

  const bufferDebugData = useMemo(() => {
    if (!isAdmin || !showBufferDebug || effectiveDuration <= 0) return null

    const ctxDebug = seekContext?.bufferDebugRef?.current
    return {
      buffered: (ctxDebug?.prefetched ?? []) as DebugRange[],
      cached: (ctxDebug?.pending ?? []) as DebugRange[],
      pooledReady: ctxDebug?.readyCount ?? 0,
      pooledTotal: ctxDebug?.totalCount ?? 0,
      playheadPercent: Math.max(0, Math.min(100, (currentTime / effectiveDuration) * 100)),
    }
  }, [bufferDebugTick, currentTime, effectiveDuration, isAdmin, showBufferDebug])
  const timeMarkers = getTimeMarkers(effectiveDuration, pixelsPerSecond)
  const timelineWidth = effectiveDuration * pixelsPerSecond
  const logMin = Math.log(MIN_PPS)
  const logMax = Math.log(MAX_PPS)
  const zoomPercent = Math.round(((Math.log(pixelsPerSecond) - logMin) / (logMax - logMin)) * 180 + 20)
  const timeToLeft = useCallback((t: number) => (t / effectiveDuration) * timelineWidth, [effectiveDuration, timelineWidth])
  const durationToWidth = useCallback((d: number) => (d / effectiveDuration) * timelineWidth, [effectiveDuration, timelineWidth])

  useEffect(() => { updateScrollState() }, [timelineWidth, updateScrollState])

  const playheadLeft = effectiveDuration > 0
    ? (currentTime / effectiveDuration) * timelineWidth
    : 0

  // Auto-scroll to keep playhead visible during playback
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container || isDragging) return
    const containerWidth = container.clientWidth
    const scrollLeft = container.scrollLeft
    const playheadRelative = playheadLeft - scrollLeft
    if (playheadRelative < 50 || playheadRelative > containerWidth - 50) {
      container.scrollTo({
        left: Math.max(0, playheadLeft - containerWidth / 2),
        behavior: 'smooth',
      })
    }
  }, [playheadLeft, isDragging])

  // Keep committed time in sync when not in hover preview
  useEffect(() => {
    if (!isHoveringRef.current && !isDragging) {
      committedTimeRef.current = currentTime
    }
  }, [currentTime, isDragging])

  const getTimeFromMouseX = useCallback((clientX: number) => {
    const container = scrollContainerRef.current
    if (!container) return 0
    const containerRect = container.getBoundingClientRect()
    const clickX = clientX - containerRect.left + container.scrollLeft
    return Math.max(0, Math.min((clickX / timelineWidth) * effectiveDuration, effectiveDuration))
  }, [timelineWidth, effectiveDuration])

  const updateSceneForTime = useCallback((t: number) => {
    for (const boundary of sceneBoundaries) {
      if (t >= boundary.start && t < boundary.end) {
        onSceneChange?.(boundary.index)
        break
      }
    }
  }, [sceneBoundaries, onSceneChange])

  const dragListenersRef = useRef<{ move: (e: MouseEvent) => void; up: (e: MouseEvent) => void } | null>(null)

  const addDragListeners = useCallback(() => {
    if (dragListenersRef.current) return
    const handleMouseMove = (e: MouseEvent) => {
      const t = getTimeFromMouseX(e.clientX)
      updateSceneForTime(t)
      if (onScrub) {
        onScrub(t)
      } else {
        onSeek(t)
      }
    }
    const handleMouseUp = (e: MouseEvent) => {
      const finalTime = getTimeFromMouseX(e.clientX)
      updateSceneForTime(finalTime)
      committedTimeRef.current = finalTime
      onSeek(finalTime)
      onScrubEnd?.()
      setIsDragging(false)
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      dragListenersRef.current = null
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    dragListenersRef.current = { move: handleMouseMove, up: handleMouseUp }
  }, [getTimeFromMouseX, onSeek, onScrub, onScrubEnd, updateSceneForTime])

  const handleTimelineMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.button !== 0) return
    clearSelection()
    const targetTime = getTimeFromMouseX(e.clientX)
    updateSceneForTime(targetTime)
    committedTimeRef.current = targetTime
    isHoveringRef.current = false
    setIsDragging(true)
    onScrubStart?.()
    addDragListeners()
    if (onScrub) {
      onScrub(targetTime)
    } else {
      onSeek(targetTime)
    }
  }, [getTimeFromMouseX, updateSceneForTime, onSeek, onScrub, onScrubStart, clearSelection, addDragListeners])

  const handlePlayheadMouseDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    isHoveringRef.current = false
    setIsDragging(true)
    onScrubStart?.()
    addDragListeners()
  }, [onScrubStart, addDragListeners])

  // Hover preview: scrub on mouse move, snap back on leave
  const handleTimelineMouseMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (isDragging || isPlaying) return
    if (clipDrag || gainDragRef.current || textOverlayDrag || exportMarkerDrag) return

    const hoverTime = getTimeFromMouseX(e.clientX)
    isHoveringRef.current = true
    onScrub?.(hoverTime)
  }, [isDragging, isPlaying, clipDrag, textOverlayDrag, exportMarkerDrag, getTimeFromMouseX, onScrub])

  const handleTimelineMouseLeave = useCallback(() => {
    if (!isHoveringRef.current || isDragging) return
    isHoveringRef.current = false
    onSeek(committedTimeRef.current)
  }, [isDragging, onSeek])

  const handleClickSeek = useCallback((clientX: number) => {
    const clickTime = getTimeFromMouseX(clientX)
    committedTimeRef.current = clickTime
    isHoveringRef.current = false
    onSeek(clickTime)
  }, [getTimeFromMouseX, onSeek])

  // Export end marker drag
  const handleExportMarkerDown = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    const state = { initialX: e.clientX, initialDuration: exportDuration ?? effectiveDuration }
    setExportMarkerDrag(state)
    exportMarkerDragRef.current = state
  }, [exportDuration, effectiveDuration])

  useEffect(() => {
    if (!exportMarkerDrag) return
    const handleMouseMove = (e: MouseEvent) => {
      const state = exportMarkerDragRef.current
      if (!state) return
      const dx = e.clientX - state.initialX
      const dt = (dx / timelineWidth) * effectiveDuration
      const newDur = Math.max(0.5, Math.min(effectiveDuration, state.initialDuration + dt))
      setExportDurationLocal(newDur)
    }
    const handleMouseUp = (e: MouseEvent) => {
      const state = exportMarkerDragRef.current
      if (state) {
        const dx = e.clientX - state.initialX
        const dt = (dx / timelineWidth) * effectiveDuration
        const newDur = Math.max(0.5, Math.min(effectiveDuration, state.initialDuration + dt))
        // If dragged to ~end, clear the export duration (use full timeline)
        const finalDur = newDur >= effectiveDuration - 0.1 ? undefined : newDur
        setCommittedExportDuration(finalDur)
        onExportDurationChange?.(finalDur)
      }
      setExportMarkerDrag(null)
      exportMarkerDragRef.current = null
      setExportDurationLocal(null)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [exportMarkerDrag, timelineWidth, effectiveDuration, onExportDurationChange])

  // Clear committed export duration once server prop catches up
  useEffect(() => {
    if (committedExportDuration === undefined && exportDuration === undefined) return
    if (committedExportDuration === undefined) return
    if (exportDuration === committedExportDuration ||
        (exportDuration != null && committedExportDuration != null && Math.abs(exportDuration - committedExportDuration) < 0.05) ||
        (exportDuration === undefined && committedExportDuration === undefined)) {
      setCommittedExportDuration(undefined)
    }
  }, [exportDuration, committedExportDuration])

  const activeExportDuration = exportDurationLocal ?? committedExportDuration ?? exportDuration

  // Collect beat markers from any music track with beat data for full-timeline overlay
  const timelineBeatMarkers = useMemo(() => {
    const markers: Array<{ time: number; tier: 'downbeat' | 'strong' | 'beat' }> = []
    for (const t of audioTracks) {
      const bd = (t as any).metadata?.beatData as BeatData | undefined
      if (!bd) continue
      const offset = t.startTime
      const trimStart = t.trimStart || 0
      const downbeatSet = new Set(bd.downbeats.map(v => Math.round(v * 1000)))
      const strongSet = new Set(bd.strongHits.map(v => Math.round(v * 1000)))
      const allTimes = new Set<number>()
      for (const v of bd.downbeats) allTimes.add(Math.round(v * 1000))
      for (const v of bd.strongHits) allTimes.add(Math.round(v * 1000))
      for (const v of bd.beats) allTimes.add(Math.round(v * 1000))
      for (const tMs of allTimes) {
        const tSec = tMs / 1000
        if (tSec < trimStart || tSec > trimStart + t.duration) continue
        const globalTime = offset + (tSec - trimStart)
        const tier = downbeatSet.has(tMs) ? 'downbeat' as const : strongSet.has(tMs) ? 'strong' as const : 'beat' as const
        markers.push({ time: globalTime, tier })
      }
    }
    return markers
  }, [audioTracks])

  const musicTracks = audioTracks.filter(t => t.type === 'music')
  const sfxTracks = audioTracks.filter(t => t.type === 'sfx')
  const voiceoverTracks = audioTracks.filter(t => t.type === 'voiceover')

  const toggleCollapse = (id: string) => setCollapsedTracks(p => ({ ...p, [id]: !p[id] }))
  const toggleMute = (id: string) => seekContext?.setTrackMuted(id, !mutedTracks[id])
  const toggleClipMute = (clipId: string) => seekContext?.setClipMuted(clipId, !clipMutes[clipId])

  const handleDeleteSelected = useCallback(() => {
    const clipIds: string[] = []
    const audioIds: string[] = []
    selectedItems.forEach(key => {
      const parts = key.split(':')
      if (parts[0] === 'clip') clipIds.push(parts[1])
      else if (parts[0] === 'audio') audioIds.push(parts[1])
    })
    if (clipIds.length > 0) onDeleteClips?.(clipIds)
    if (audioIds.length > 0) onDeleteAudioTracks?.(audioIds)
    clearSelection()
  }, [selectedItems, onDeleteClips, onDeleteAudioTracks, clearSelection])

  // Split at playhead: find the clip under the current time and split it
  const handleSplitAtPlayhead = useCallback(async () => {
    if (!currentTime || currentTime <= 0) return
    // Find clip at playhead
    let cumTime = 0
    for (const scene of scenes) {
      const dur = getEffectiveDuration(scene)
      if (currentTime > cumTime && currentTime < cumTime + dur - 0.1) {
        const splitAt = (scene.trimStart || 0) + (currentTime - cumTime) * (scene.speed || 1)
        try {
          await splitClipMutation({ clipId: scene._id as any, splitAtSeconds: splitAt })
        } catch (err) {
          console.warn('[Timeline] Split failed:', err)
        }
        return
      }
      cumTime += dur
    }
  }, [currentTime, scenes, splitClipMutation])

  useEffect(() => {
    const el = containerRef.current
    if (!el) return
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      switch (e.key) {
        case ' ':
          e.preventDefault()
          onPlayPause?.()
          break
        case 'ArrowLeft':
          e.preventDefault()
          onSeek(Math.max(0, currentTime - (e.shiftKey ? 5 : 1)))
          break
        case 'ArrowRight':
          e.preventDefault()
          onSeek(Math.min(effectiveDuration, currentTime + (e.shiftKey ? 5 : 1)))
          break
        case 'Delete':
        case 'Backspace':
          if (selectedItems.size > 0) {
            e.preventDefault()
            handleDeleteSelected()
          }
          break
        case 'Escape':
          clearSelection()
          break
        case 's':
          if (!e.metaKey && !e.ctrlKey) {
            e.preventDefault()
            handleSplitAtPlayhead()
          }
          break
        case 'z':
          if (e.metaKey || e.ctrlKey) {
            e.preventDefault()
            if (e.shiftKey) onRedo?.()
            else onUndo?.()
          }
          break
      }
    }
    el.addEventListener('keydown', handleKeyDown)
    return () => el.removeEventListener('keydown', handleKeyDown)
  }, [currentTime, effectiveDuration, selectedItems, onPlayPause, onSeek, onUndo, onRedo, handleDeleteSelected, handleSplitAtPlayhead, clearSelection])

  // -- Clip drag handlers (trim + reorder) --
  const handleClipDragStart = useCallback((e: React.MouseEvent, type: ClipDragState['type'], clipId: string, clipIndex: number) => {
    e.stopPropagation()
    e.preventDefault()
    const boundary = sceneBoundaries[clipIndex]
    if (!boundary) return
    const scene = boundary.scene
    const state: ClipDragState = {
      type,
      clipId,
      clipIndex,
      initialX: e.clientX,
      initialLeft: timeToLeft(boundary.start),
      initialWidth: durationToWidth(getEffectiveDuration(scene)),
      initialTrimStart: scene.trimStart || 0,
      initialTrimEnd: scene.trimEnd || 0,
      currentX: e.clientX,
      targetIndex: clipIndex,
      snappedTo: null,
      started: type !== 'reorder',
    }
    clipDragRef.current = state
    setClipDrag(state)
    if (type === 'trim-left' || type === 'trim-right') {
      onScrubStart?.()
      const seekTime = type === 'trim-left' ? boundary.start : boundary.end
      if (onScrub) onScrub(seekTime); else onSeek(seekTime)
    }
  }, [sceneBoundaries, timeToLeft, durationToWidth, onScrubStart, onScrub, onSeek])

  // Compute reorder target index and insert position from mouse position
  const getReorderTarget = useCallback((mouseX: number, dragIndex: number) => {
    const container = scrollContainerRef.current
    if (!container) return { targetIndex: dragIndex, insertLeft: 0 }
    const containerRect = container.getBoundingClientRect()
    const xInTimeline = mouseX - containerRect.left + container.scrollLeft
    let target = sceneBoundaries.length
    for (let i = 0; i < sceneBoundaries.length; i++) {
      const midX = timeToLeft(sceneBoundaries[i].start) + durationToWidth(getEffectiveDuration(sceneBoundaries[i].scene)) / 2
      if (xInTimeline < midX) { target = i; break }
    }
    if (target > dragIndex) target = Math.min(target, sceneBoundaries.length)
    const insertLeft = target < sceneBoundaries.length
      ? timeToLeft(sceneBoundaries[target].start)
      : timeToLeft(sceneBoundaries[sceneBoundaries.length - 1].start) + durationToWidth(getEffectiveDuration(sceneBoundaries[sceneBoundaries.length - 1].scene))
    return { targetIndex: target, insertLeft }
  }, [sceneBoundaries, timeToLeft, durationToWidth])

  // Snap helper: find nearest edge within threshold
  const findSnap = useCallback((leftPx: number, widthPx: number, dragIndex: number): number | null => {
    const edges: number[] = []
    for (let i = 0; i < sceneBoundaries.length; i++) {
      if (i === dragIndex) continue
      edges.push(timeToLeft(sceneBoundaries[i].start))
      edges.push(timeToLeft(sceneBoundaries[i].start) + durationToWidth(getEffectiveDuration(sceneBoundaries[i].scene)))
    }
    edges.push(0)
    let best: number | null = null
    let bestDist = SNAP_THRESHOLD + 1
    for (const edge of edges) {
      const dLeft = Math.abs(leftPx - edge)
      const dRight = Math.abs(leftPx + widthPx - edge)
      if (dLeft < bestDist) { bestDist = dLeft; best = edge }
      if (dRight < bestDist) { bestDist = dRight; best = edge }
    }
    return best
  }, [sceneBoundaries, timeToLeft, durationToWidth])

  useEffect(() => {
    if (!clipDrag) return
    const handleMouseMove = (e: MouseEvent) => {
      const state = clipDragRef.current
      if (!state) return
      const dx = e.clientX - state.initialX
      if (state.type === 'reorder' && !state.started && Math.abs(dx) < DRAG_START_THRESHOLD) return
      const updated = { ...state, currentX: e.clientX, started: true }

      if (state.type === 'reorder') {
        const { targetIndex } = getReorderTarget(e.clientX, state.clipIndex)
        updated.targetIndex = targetIndex
        updated.snappedTo = findSnap(state.initialLeft + dx, state.initialWidth, state.clipIndex)
      }

      clipDragRef.current = updated
      setClipDrag(updated)

      if (state.type === 'trim-left' || state.type === 'trim-right') {
        const boundary = sceneBoundaries[state.clipIndex]
        if (boundary) {
          const scene = boundary.scene
          const speed = scene.speed || 1
          const deltaTime = ((e.clientX - state.initialX) / timelineWidth) * effectiveDuration
          let seekTime: number
          if (state.type === 'trim-left') {
            const newTrimStart = Math.max(0, state.initialTrimStart + deltaTime)
            seekTime = boundary.start + (newTrimStart - state.initialTrimStart) / speed
          } else {
            const newTrimEnd = state.initialTrimEnd - deltaTime
            seekTime = boundary.end - (newTrimEnd - state.initialTrimEnd) / speed
          }
          seekTime = Math.max(boundary.start, Math.min(seekTime, boundary.end))
          if (onScrub) onScrub(seekTime); else onSeek(seekTime)
        }
      }
    }

    const handleMouseUp = () => {
      const state = clipDragRef.current
      if (!state || !state.started) {
        clipDragRef.current = null
        setClipDrag(null)
        return
      }

      const wasTrim = state.type === 'trim-left' || state.type === 'trim-right'

      if (state.type === 'reorder') {
        let tgt = state.targetIndex
        if (tgt > state.clipIndex) tgt--
        if (tgt !== state.clipIndex && onClipReorder) {
          const ghostLeft = state.initialLeft + (state.currentX - state.initialX)
          setPendingReorder({ dragIndex: state.clipIndex, targetIndex: state.targetIndex, ghostLeft, settled: false })
          const order = sceneBoundaries.map(b => b.scene._id)
          const [moved] = order.splice(state.clipIndex, 1)
          const insertAt = tgt > state.clipIndex ? tgt : tgt
          order.splice(insertAt, 0, moved)
          onClipReorder(order)
        }
      } else {
        const boundary = sceneBoundaries[state.clipIndex]
        if (boundary && onClipTrimUpdate) {
          const scene = boundary.scene
          const deltaTime = ((state.currentX - state.initialX) / timelineWidth) * effectiveDuration
          let newTrimStart = state.initialTrimStart
          let newTrimEnd = state.initialTrimEnd
          if (state.type === 'trim-left') {
            newTrimStart = Math.max(0, state.initialTrimStart + deltaTime)
            if (newTrimStart + newTrimEnd >= scene.duration - 0.5) newTrimStart = scene.duration - 0.5 - newTrimEnd
          } else {
            newTrimEnd = state.initialTrimEnd - deltaTime
            if (newTrimStart + newTrimEnd >= scene.duration - 0.5) newTrimEnd = scene.duration - 0.5 - newTrimStart
          }
          const finalTrimStart = Math.max(0, newTrimStart)
          const finalTrimEnd = newTrimEnd
          setCommittedClipTrim({ clipId: state.clipId, trimStart: finalTrimStart, trimEnd: finalTrimEnd })
          onClipTrimUpdate(state.clipId, finalTrimStart, finalTrimEnd)
        }
      }

      clipDragRef.current = null
      setClipDrag(null)
      if (wasTrim) onScrubEnd?.()
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
    }
  }, [clipDrag, sceneBoundaries, onClipReorder, onClipTrimUpdate, getReorderTarget, findSnap, timelineWidth, effectiveDuration, onScrub, onScrubEnd, onSeek, getTimeFromMouseX])

  // Text overlay drag effect
  useEffect(() => {
    if (!textOverlayDrag) return
    textOverlayDidDrag.current = false
    const handleMouseMove = (e: MouseEvent) => {
      if (Math.abs(e.clientX - textOverlayDrag.initialX) > DRAG_START_THRESHOLD) {
        textOverlayDidDrag.current = true
      }
      const deltaTime = ((e.clientX - textOverlayDrag.initialX) / timelineWidth) * effectiveDuration
      if (textOverlayDrag.type === 'move') {
        const newStart = Math.max(0, Math.min(textOverlayDrag.initialStartTime + deltaTime, effectiveDuration - textOverlayDrag.initialDuration))
        textOverlayLocalRef.current = { startTime: newStart, duration: textOverlayDrag.initialDuration }
        setTextOverlayLocal({ startTime: newStart, duration: textOverlayDrag.initialDuration })
      } else if (textOverlayDrag.type === 'resize-left') {
        const newStart = Math.max(0, textOverlayDrag.initialStartTime + deltaTime)
        const rightEdge = textOverlayDrag.initialStartTime + textOverlayDrag.initialDuration
        const clampedStart = Math.min(newStart, rightEdge - 0.5)
        const newDur = rightEdge - clampedStart
        textOverlayLocalRef.current = { startTime: clampedStart, duration: newDur }
        setTextOverlayLocal({ startTime: clampedStart, duration: newDur })
      } else {
        const newDur = Math.max(0.5, textOverlayDrag.initialDuration + deltaTime)
        textOverlayLocalRef.current = { startTime: textOverlayDrag.initialStartTime, duration: newDur }
        setTextOverlayLocal({ startTime: textOverlayDrag.initialStartTime, duration: newDur })
      }
    }
    const handleMouseUp = () => {
      if (!textOverlayDidDrag.current && textOverlayDrag.type === 'move') {
        onOverlaySelect?.(selectedOverlayId === textOverlayDrag.overlayId ? null : textOverlayDrag.overlayId)
      } else {
        const { startTime, duration } = textOverlayLocalRef.current
        textOverlayCommittedRef.current = { overlayId: textOverlayDrag.overlayId, startTime, duration }
        if (onTextOverlayUpdate) {
          onTextOverlayUpdate(textOverlayDrag.overlayId, startTime, duration)
        }
      }
      setTextOverlayDrag(null)
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp) }
  }, [textOverlayDrag, timelineWidth, effectiveDuration, onTextOverlayUpdate, onOverlaySelect, selectedOverlayId, getTimeFromMouseX, onSeek])

  // Clear committed clip trim once server props catch up
  useEffect(() => {
    if (!committedClipTrim) return
    const scene = scenes.find(s => s._id === committedClipTrim.clipId)
    if (!scene) { setCommittedClipTrim(null); return }
    if (Math.abs((scene.trimStart || 0) - committedClipTrim.trimStart) < 0.01 &&
        Math.abs((scene.trimEnd || 0) - committedClipTrim.trimEnd) < 0.01) {
      setCommittedClipTrim(null)
    }
  }, [scenes, committedClipTrim])

  // Compute local trim preview during drag or from committed state
  const getClipTrimPreview = useCallback((clipIndex: number) => {
    const state = clipDragRef.current
    if (state && state.started && state.clipIndex === clipIndex &&
        (state.type === 'trim-left' || state.type === 'trim-right')) {
      const scene = sceneBoundaries[clipIndex]?.scene
      if (!scene) return null
      const deltaTime = ((state.currentX - state.initialX) / timelineWidth) * effectiveDuration
      let trimStart = state.initialTrimStart
      let trimEnd = state.initialTrimEnd
      if (state.type === 'trim-left') {
        trimStart = Math.max(0, state.initialTrimStart + deltaTime)
        if (trimStart + trimEnd >= scene.duration - 0.5) trimStart = scene.duration - 0.5 - trimEnd
      } else {
        trimEnd = state.initialTrimEnd - deltaTime
        if (trimStart + trimEnd >= scene.duration - 0.5) trimEnd = scene.duration - 0.5 - trimStart
      }
      trimStart = Math.max(0, trimStart)
      const effDur = (scene.duration - trimStart - trimEnd) / (scene.speed || 1)
      return { trimStart, trimEnd, effectiveDuration: effDur }
    }
    if (committedClipTrim) {
      const boundary = sceneBoundaries[clipIndex]
      if (boundary && boundary.scene._id === committedClipTrim.clipId) {
        const scene = boundary.scene
        const effDur = (scene.duration - committedClipTrim.trimStart - committedClipTrim.trimEnd) / (scene.speed || 1)
        return { trimStart: committedClipTrim.trimStart, trimEnd: committedClipTrim.trimEnd, effectiveDuration: effDur }
      }
    }
    return null
  }, [sceneBoundaries, timelineWidth, effectiveDuration, committedClipTrim])

  if (scenes.length === 0 && audioTracks.length === 0) return null

  const videoH = collapsedTracks['video'] ? 16 : VIDEO_TRACK_HEIGHT

  const clipContentDuration = sceneBoundaries.length > 0
    ? sceneBoundaries[sceneBoundaries.length - 1].end
    : effectiveDuration

  const skipPrev = () => {
    for (let i = sceneBoundaries.length - 1; i >= 0; i--) {
      if (sceneBoundaries[i].start < currentTime - 0.3) {
        onSeek(sceneBoundaries[i].start)
        onSceneChange?.(sceneBoundaries[i].index)
        return
      }
    }
    onSeek(0)
  }

  const skipNext = () => {
    for (let i = 0; i < sceneBoundaries.length; i++) {
      if (sceneBoundaries[i].start > currentTime + 0.1) {
        onSeek(sceneBoundaries[i].start)
        onSceneChange?.(sceneBoundaries[i].index)
        return
      }
    }
  }

  if (!isExpanded) {
    return (
      <div ref={containerRef} tabIndex={0} className="bg-surface select-none outline-none border-t border-purple-900/40" onClick={(e) => { if (e.target === e.currentTarget) clearSelection() }}>
        {/* Collapsed scrub bar */}
        <div className="flex items-center gap-2 px-2 h-9">
          {/* Transport controls */}
          <div className="flex items-center shrink-0 rounded-md overflow-hidden border border-purple-800/30 bg-surface-elevated/50 h-6">
            <button onClick={skipPrev} className="grid h-full w-6 place-items-center text-surface-muted hover:text-purple-300 hover:bg-purple-900/30 transition-colors" title="Previous clip">
              <SkipBack size={11} />
            </button>
            <div className="w-px h-3.5 bg-purple-800/30" />
            <button onClick={() => onPlayPause?.()} className="grid h-full w-7 place-items-center text-surface-foreground hover:text-purple-300 hover:bg-purple-900/30 transition-colors" title={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? <Pause size={13} /> : <Play size={13} />}
            </button>
            <div className="w-px h-3.5 bg-purple-800/30" />
            <button onClick={skipNext} className="grid h-full w-6 place-items-center text-surface-muted hover:text-purple-300 hover:bg-purple-900/30 transition-colors" title="Next clip">
              <SkipForward size={11} />
            </button>
          </div>
          {/* Time */}
          <span className="text-[10px] text-surface-muted font-mono tabular-nums w-10 shrink-0 ml-0.5">{formatTime(currentTime)}</span>
          {/* Scrub track */}
          <div
            className="flex-1 relative h-5 cursor-pointer group"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect()
              const pct = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
              const t = pct * clipContentDuration
              const wasPlaying = isPlaying
              onSeek(t)
              if (wasPlaying) onPlayPause?.()
              for (const boundary of sceneBoundaries) {
                if (t >= boundary.start && t < boundary.end) { onSceneChange?.(boundary.index); break }
              }
            }}
          >
            {/* Clip rectangles */}
            <div className="absolute inset-y-1 left-0 right-0 overflow-hidden bg-surface-elevated/60">
              {sceneBoundaries.map((boundary, index) => {
                const leftPct = (boundary.start / clipContentDuration) * 100
                const widthPct = (getEffectiveDuration(boundary.scene) / clipContentDuration) * 100
                const isActive = currentSceneIndex === index
                const st = boundary.scene.videoStatus
                const isGenerating = st === 'generating_video' || st === 'generating_image'
                const statusBg = st === 'completed' ? 'bg-purple-700/60'
                  : st === 'failed' || st === 'cancelled' ? 'bg-red-700/60'
                  : isGenerating ? 'bg-purple-600/50'
                  : st === 'image_ready' ? 'bg-indigo-700/50'
                  : 'bg-surface-border/60'
                return (
                  <div
                    key={boundary.scene._id}
                    className={`absolute top-0 bottom-0 overflow-hidden ${isActive ? 'ring-1 ring-inset ring-purple-400/50' : ''}`}
                    style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
                  >
                    <div className={`w-full h-full ${statusBg} ${isGenerating ? 'animate-pulse' : ''}`} />
                    {index > 0 && <div className="absolute left-0 top-0 bottom-0 w-px bg-surface/60" />}
                  </div>
                )
              })}
              {/* Progress fill */}
              <div className="absolute top-0 bottom-0 left-0 bg-purple-400/10 pointer-events-none" style={{ width: `${clipContentDuration > 0 ? Math.min((currentTime / clipContentDuration) * 100, 100) : 0}%`, transition: isPlaying ? 'width 300ms linear' : 'none' }} />
            </div>
            {/* Playhead */}
            <div className="absolute top-0 bottom-0 pointer-events-none" style={{ left: `${clipContentDuration > 0 ? Math.min((currentTime / clipContentDuration) * 100, 100) : 0}%`, transition: isPlaying ? 'left 300ms linear' : 'none' }}>
              <div className="absolute -left-px top-0.5 bottom-0.5 w-0.5 bg-purple-300 shadow-[0_0_4px_rgba(168,85,247,0.5)]" />
            </div>
          </div>
          {/* End time */}
          <span className="text-[10px] text-surface-muted font-mono tabular-nums w-10 text-right shrink-0">{formatTime(clipContentDuration)}</span>
        </div>
        <CollapsedTranscriptWords audioTracks={audioTracks} totalDuration={clipContentDuration} currentTime={currentTime} onSeek={onSeek} />
        {/* Centered expand arrow */}
        {onToggleExpand && (
          <div className="relative flex items-center justify-center h-6 bg-gradient-to-r from-purple-950/20 via-purple-800/35 to-purple-950/20 cursor-pointer group/expand" onClick={onToggleExpand} title="Expand timeline">
            <span className="absolute left-3 text-[9px] font-medium tracking-widest uppercase text-purple-400/40 opacity-0 group-hover/expand:opacity-100 transition-opacity duration-200">Expand Timeline</span>
            <ChevronDown size={14} strokeWidth={2} className="text-purple-400/70 group-hover/expand:text-purple-300" />
          </div>
        )}
      </div>
    )
  }

  return (
    <div ref={containerRef} tabIndex={0} className="bg-surface select-none outline-none flex flex-col" onClick={(e) => { if (e.target === e.currentTarget) clearSelection() }}>
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-2 h-8 border-b border-surface-border shrink-0">
        {/* Transport */}
        <div className="flex items-center shrink-0 rounded-md overflow-hidden border border-surface-border bg-surface-elevated/50 h-6">
          <button onClick={skipPrev} className="grid h-full w-6 place-items-center text-surface-muted hover:text-white hover:bg-surface-elevated transition-colors" title="Previous clip">
            <SkipBack size={11} />
          </button>
          <div className="w-px h-3.5 bg-surface-border" />
          <button onClick={() => onPlayPause?.()} className="grid h-full w-7 place-items-center text-surface-foreground hover:text-white hover:bg-surface-elevated transition-colors" title={isPlaying ? 'Pause' : 'Play'}>
            {isPlaying ? <Pause size={13} /> : <Play size={13} />}
          </button>
          <div className="w-px h-3.5 bg-surface-border" />
          <button onClick={skipNext} className="grid h-full w-6 place-items-center text-surface-muted hover:text-white hover:bg-surface-elevated transition-colors" title="Next clip">
            <SkipForward size={11} />
          </button>
        </div>
        <span className="text-[10px] leading-none text-surface-foreground font-mono tabular-nums shrink-0 ml-1.5">{formatTime(currentTime)} <span className="text-surface-muted/60">/</span> {formatTime(effectiveDuration)}</span>
        <div className="flex-1" />
        {workflowId && (
          <span className="text-[10px] leading-none shrink-0">
            <button
              onClick={() => handleTimelineModeChange('ripple')}
              disabled={isChangingTimelineMode}
              className={`transition-colors disabled:opacity-50 ${activeTimelineMode === 'ripple' ? 'text-surface-foreground' : 'text-surface-muted/50 hover:text-surface-muted'}`}
              title="Ripple timeline edits"
            >
              Ripple
            </button>
            <span className="text-surface-muted/30 mx-1">/</span>
            <button
              onClick={() => handleTimelineModeChange('preserve_positions')}
              disabled={isChangingTimelineMode}
              className={`transition-colors disabled:opacity-50 ${activeTimelineMode === 'preserve_positions' ? 'text-surface-foreground' : 'text-surface-muted/50 hover:text-surface-muted'}`}
              title="Fixed clip positions"
            >
              Fixed
            </button>
          </span>
        )}
        {/* Split at playhead */}
        <button
          onClick={handleSplitAtPlayhead}
          className="grid h-6 w-6 place-items-center text-surface-muted hover:text-white hover:bg-surface-elevated rounded transition-colors shrink-0"
          title="Split clip at playhead (S)"
        >
          <Scissors size={11} />
        </button>
        {/* Clip count */}
        <span className="text-[10px] leading-none text-surface-muted shrink-0">{scenes.length} clips</span>
        {isAdmin && (
          <>
            <div className="w-px h-4 bg-surface-border mx-0.5" />
            <button
              onClick={toggleBufferDebug}
              className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-none text-[9px] transition-colors shrink-0 ${showBufferDebug ? 'bg-emerald-900/60 text-emerald-200 border border-emerald-700/70' : 'bg-surface-elevated text-surface-foreground border border-surface-border hover:text-white'}`}
              title="Toggle buffer debug in timeline"
            >
              <Gauge size={10} />
              {showBufferDebug ? 'Debug On' : 'Debug Off'}
            </button>
          </>
        )}
        <div className="w-px h-4 bg-surface-border mx-0.5" />
        {/* Zoom controls */}
        <div className="flex items-center gap-1.5 shrink-0 bg-surface/80 h-6 px-1.5">
          <button onClick={() => zoomOut()} className="grid h-4 w-4 place-items-center text-surface-muted hover:text-white transition-colors" title="Zoom out">
            <ZoomOut size={11} />
          </button>
          <div className="w-44">
            <input
              type="range"
              min={20}
              max={200}
              step={1}
              value={zoomPercent}
              onChange={(e) => {
                const pct = Number(e.target.value)
                const t = (pct - 20) / 180
                const pps = Math.exp(Math.log(MIN_PPS) + t * (Math.log(MAX_PPS) - Math.log(MIN_PPS)))
                setZoomFromSlider(pps)
              }}
              className="timeline-zoom-slider w-full"
              title={`Zoom: ${zoomPercent}%`}
            />
          </div>
          <button onClick={() => zoomIn()} className="grid h-4 w-4 place-items-center text-surface-muted hover:text-white transition-colors" title="Zoom in">
            <ZoomIn size={11} />
          </button>
          <span className="text-[9px] text-surface-foreground font-mono tabular-nums w-10 text-right">{zoomPercent}%</span>
        </div>
      </div>

      {/* Track content */}
      <div className="flex flex-1 min-h-0">
        {/* Fixed labels column */}
        <div className="shrink-0 bg-surface/80" style={{ width: TRACK_LABEL_WIDTH }}>
          <div className="border-b border-r border-surface-border" style={{ height: TIME_RULER_HEIGHT }} />
          <div className="border-b border-r border-surface-border" style={{ height: collapsedTracks['video'] ? 16 : VIDEO_TRACK_HEIGHT }}>
            <div className="flex items-center gap-1 px-1.5 h-full">
              <button onClick={() => toggleCollapse('video')} className="shrink-0 text-surface-muted hover:text-surface-foreground">
                {collapsedTracks['video'] ? <ChevronRight size={9} /> : <ChevronDown size={9} />}
              </button>
              <span className="text-surface-muted"><Film size={11} /></span>
              <span className="text-[9px] text-surface-muted truncate">Video</span>
            </div>
          </div>
          {hasVideoAudio && (
            <div className="border-b border-r border-surface-border" style={{ height: collapsedTracks['videoAudio'] ? 16 : VIDEO_AUDIO_TRACK_HEIGHT }}>
              <AudioTrackLabel label="Clip Audio" icon={<Volume2 size={11} />} collapsed={!!collapsedTracks['videoAudio']} muted={!!mutedTracks['videoAudio']} volume={seekContext?.audioMix.trackVolumes['videoAudio'] ?? 1} onToggleCollapse={() => toggleCollapse('videoAudio')} onToggleMute={() => toggleMute('videoAudio')} onVolumeChange={(v) => seekContext?.setTrackVolume('videoAudio', v)} dark />
            </div>
          )}
          {musicTracks.length > 0 && (
            <div className="border-b border-r border-surface-border" style={{ height: collapsedTracks['music'] ? 16 : AUDIO_TRACK_HEIGHT }}>
              <AudioTrackLabel label="Audio" icon={<Music size={11} />} collapsed={!!collapsedTracks['music']} muted={!!mutedTracks['music']} volume={seekContext?.audioMix.trackVolumes['music'] ?? 1} onToggleCollapse={() => toggleCollapse('music')} onToggleMute={() => toggleMute('music')} onVolumeChange={(v) => seekContext?.setTrackVolume('music', v)} dark />
            </div>
          )}
          {sfxTracks.length > 0 && (
            <div className="border-b border-r border-surface-border" style={{ height: collapsedTracks['sfx'] ? 16 : AUDIO_TRACK_HEIGHT }}>
              <AudioTrackLabel label="SFX" icon={<Zap size={11} />} collapsed={!!collapsedTracks['sfx']} muted={!!mutedTracks['sfx']} volume={seekContext?.audioMix.trackVolumes['sfx'] ?? 1} onToggleCollapse={() => toggleCollapse('sfx')} onToggleMute={() => toggleMute('sfx')} onVolumeChange={(v) => seekContext?.setTrackVolume('sfx', v)} dark />
            </div>
          )}
          {voiceoverTracks.length > 0 && (
            <div className="border-b border-r border-surface-border" style={{ height: collapsedTracks['voice'] ? 16 : AUDIO_TRACK_HEIGHT }}>
              <AudioTrackLabel label="Voice" icon={<Mic size={11} />} collapsed={!!collapsedTracks['voice']} muted={!!mutedTracks['voice']} volume={seekContext?.audioMix.trackVolumes['voice'] ?? 1} onToggleCollapse={() => toggleCollapse('voice')} onToggleMute={() => toggleMute('voice')} onVolumeChange={(v) => seekContext?.setTrackVolume('voice', v)} dark />
            </div>
          )}
          {textOverlays.length > 0 && (
            <div className="border-b border-r border-surface-border" style={{ height: TEXT_OVERLAY_TRACK_HEIGHT }}>
              <div className="flex items-center gap-1 px-1.5 h-full">
                <span className="text-surface-muted"><Type size={11} /></span>
                <span className="text-[9px] text-surface-muted truncate">Text</span>
              </div>
            </div>
          )}
        </div>

        {/* Scrollable content */}
        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-x-auto overflow-y-hidden [&::-webkit-scrollbar]:hidden"
          style={{ scrollbarWidth: 'none' }}
        >
          <div
            ref={timelineRef}
            className={`relative ${isDragging ? 'cursor-grabbing' : ''}`}
            style={{ width: timelineWidth }}
            onMouseDown={handleTimelineMouseDown}
            onMouseMove={handleTimelineMouseMove}
            onMouseLeave={handleTimelineMouseLeave}
          >
            {/* Time Ruler */}
            <div className="border-b border-surface-border relative bg-surface" style={{ height: TIME_RULER_HEIGHT }}>
              {timeMarkers.map((time) => (
                <div key={time} className="absolute top-0 bottom-0" style={{ left: timeToLeft(time) }}>
                  <div className="h-full w-px bg-surface-border" />
                  <span className="absolute top-1 left-1 text-[9px] text-surface-muted font-mono tabular-nums whitespace-nowrap">{formatTime(time)}</span>
                </div>
              ))}
              {isAdmin && showBufferDebug && bufferDebugData && (
                <>
                  {bufferDebugData.buffered.map((range, i) => (
                    <div
                      key={`debug-buffered-${i}`}
                      className="absolute bottom-1 h-1 bg-emerald-400/80 rounded-full pointer-events-none"
                      style={{
                        left: `${Math.max(0, Math.min(100, (range.start / effectiveDuration) * 100))}%`,
                        width: `${Math.max(0.15, Math.min(100, ((range.end - range.start) / effectiveDuration) * 100))}%`,
                      }}
                    />
                  ))}
                  {bufferDebugData.cached.map((range, i) => (
                    <div
                      key={`debug-cached-${i}`}
                      className="absolute bottom-0 h-1 bg-amber-400/80 rounded-full pointer-events-none"
                      style={{
                        left: `${Math.max(0, Math.min(100, (range.start / effectiveDuration) * 100))}%`,
                        width: `${Math.max(0.15, Math.min(100, ((range.end - range.start) / effectiveDuration) * 100))}%`,
                      }}
                    />
                  ))}
                  <div className="absolute top-0 bottom-0 w-px bg-white/80 pointer-events-none" style={{ left: `${bufferDebugData.playheadPercent}%` }} />
                  <div className="absolute right-2 bottom-0.5 text-[8px] font-mono text-emerald-300 bg-surface/90 px-1 rounded pointer-events-none">
                    {bufferDebugData.pooledReady}/{bufferDebugData.pooledTotal}
                  </div>
                </>
              )}
            </div>

            {/* Video Track */}
            <div className="border-b border-surface-border overflow-hidden" style={{ height: videoH }}>
              {collapsedTracks['video'] ? (
                <div className="relative w-full h-full">
                  {sceneBoundaries.map((boundary, index) => {
                    const left = timeToLeft(boundary.start)
                    const width = durationToWidth(getEffectiveDuration(boundary.scene))
                    const hasVideo = boundary.scene.videoStatus === 'completed'
                    const thumb = hasVideo
                      ? (boundary.scene.firstFrameUrl || (boundary.scene.videoUrl && videoThumbs.get(boundary.scene.videoUrl)) || null)
                      : boundary.scene.startKeyframeUrl || boundary.scene.storyboardImageUrl || null
                    return (
                      <div
                        key={boundary.scene._id}
                        data-clip-status={boundary.scene.videoStatus}
                        data-clip-title={boundary.scene.title || `Clip ${index + 1}`}
                        className={`absolute top-0.5 bottom-0.5 rounded-sm overflow-hidden cursor-pointer ${!thumb ? 'bg-surface-border' : ''} ${currentSceneIndex === index ? 'ring-1 ring-sky-400/50' : ''}`}
                        style={{ left: left + 1, width: width - 2, borderLeft: boundary.scene.linkGroupId ? `2px solid ${linkGroupColor(boundary.scene.linkGroupId)}` : undefined }}
                        onClick={(e) => { e.stopPropagation(); onSeek(getTimeFromMouseX(e.clientX)) }}
                      >
                        {thumb && <img src={thumb} alt="" className="w-full h-full object-cover" draggable={false} />}
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="relative w-full h-full bg-surface">
                  {sceneBoundaries.map((boundary, index) => {
                    const trimPreview = getClipTrimPreview(index)
                    const effDur = trimPreview ? trimPreview.effectiveDuration : getEffectiveDuration(boundary.scene)
                    const left = trimPreview
                      ? timeToLeft(boundary.start) + durationToWidth((trimPreview.trimStart - (boundary.scene.trimStart || 0)) / (boundary.scene.speed || 1))
                      : timeToLeft(boundary.start)
                    const width = durationToWidth(effDur)
                    const isActive = currentSceneIndex === index
                    const hasVideo = boundary.scene.videoStatus === 'completed' && boundary.scene.videoUrl
                    const isSolidColor = !!boundary.scene.solidColor
                    const prevScene = index > 0 ? sceneBoundaries[index - 1].scene : null
                    const hasContinuity = hasClipContinuity(boundary.scene, prevScene)
                    const isGenerating = boundary.scene.videoStatus === 'generating_video' || boundary.scene.videoStatus === 'generating_image'
                    const isStale = (boundary.scene.staleReasons?.length ?? 0) > 0
                    const thumbnailUrl = hasVideo
                      ? (boundary.scene.firstFrameUrl || (boundary.scene.videoUrl && videoThumbs.get(boundary.scene.videoUrl)) || null)
                      : boundary.scene.startKeyframeUrl || boundary.scene.storyboardImageUrl || boundary.scene.firstFrameUrl || null
                    const hasSpeed = boundary.scene.speed && boundary.scene.speed !== 1
                    const hasEffects = boundary.scene.effects && (boundary.scene.effects.colorGrade || boundary.scene.effects.letterbox || boundary.scene.effects.fadeIn || boundary.scene.effects.fadeOut || boundary.scene.effects.vignette || boundary.scene.effects.blur || boundary.scene.effects.bloom || boundary.scene.effects.chromaticAberration || boundary.scene.effects.vhs || boundary.scene.effects.shake || boundary.scene.effects.kenBurns)
                    const hasTransition = !!boundary.scene.transition
                    const rawDiffersFromEffective = effDur !== boundary.scene.duration
                    const isDraggedForReorder = clipDrag?.type === 'reorder' && clipDrag.started && clipDrag.clipIndex === index

                    const reorderInfo = (clipDrag?.type === 'reorder' && clipDrag.started)
                      ? { dragIndex: clipDrag.clipIndex, targetIndex: clipDrag.targetIndex }
                      : pendingReorder
                    let reorderOffset = 0
                    if (reorderInfo && index !== reorderInfo.dragIndex) {
                      const dragIdx = reorderInfo.dragIndex
                      const tgtIdx = reorderInfo.targetIndex
                      const draggedW = durationToWidth(getEffectiveDuration(sceneBoundaries[dragIdx].scene))
                      if (tgtIdx > dragIdx && index > dragIdx && index < tgtIdx) {
                        reorderOffset = -draggedW
                      } else if (tgtIdx < dragIdx && index >= tgtIdx && index < dragIdx) {
                        reorderOffset = draggedW
                      }
                    } else if (pendingReorder && !clipDrag && index === pendingReorder.dragIndex) {
                      if (!pendingReorder.settled) {
                        reorderOffset = pendingReorder.ghostLeft - timeToLeft(sceneBoundaries[pendingReorder.dragIndex].start)
                      } else {
                        const dragIdx = pendingReorder.dragIndex
                        const tgtIdx = pendingReorder.targetIndex
                        const draggedW = durationToWidth(getEffectiveDuration(sceneBoundaries[dragIdx].scene))
                        const insertLeft = tgtIdx < sceneBoundaries.length
                          ? timeToLeft(sceneBoundaries[tgtIdx].start)
                          : timeToLeft(sceneBoundaries[sceneBoundaries.length - 1].start) + durationToWidth(getEffectiveDuration(sceneBoundaries[sceneBoundaries.length - 1].scene))
                        const gapLeft = tgtIdx > dragIdx ? insertLeft - draggedW : insertLeft
                        reorderOffset = gapLeft - timeToLeft(sceneBoundaries[dragIdx].start)
                      }
                    }

                    return (
                      <div key={boundary.scene._id}>
                        {/* Continuation connector between clips */}
                        {hasContinuity && !hasTransition && !isDraggedForReorder && (
                          <div
                            className="absolute z-10 flex items-center pointer-events-none"
                            style={{ left: left + reorderOffset - 1, top: 4, bottom: 4, width: 6, transition: suppressTransition ? 'none' : 'left 200ms ease-out' }}
                          >
                            <div className="w-full h-full rounded-sm bg-teal-400/40" />
                          </div>
                        )}
                        {/* Transition overlap zone between clips */}
                        {hasTransition && !isDraggedForReorder && (() => {
                          const t = boundary.scene.transition!
                          const prevBoundary = index > 0 ? sceneBoundaries[index - 1] : null
                          const prevEffDur = prevBoundary ? getEffectiveDuration(prevBoundary.scene) : 0
                          const overlapDur = Math.min(t.duration, prevEffDur * 0.5)
                          const overlapW = Math.max(durationToWidth(overlapDur), 16)
                          const overlapLeft = left + reorderOffset - overlapW / 2
                          const isBlack = t.type === 'dip_to_black'
                          const isWhite = t.type === 'dip_to_white'
                          const isWipe = t.type.startsWith('wipe_')
                          const isGL = t.type.startsWith('gl_')
                          const label = t.type === 'crossfade' ? 'XF' : isBlack ? 'DtB' : isWhite ? 'DtW' : t.type === 'wipe_left' ? 'WL' : t.type === 'wipe_right' ? 'WR' : isGL ? t.type.replace('gl_', '').slice(0, 3).toUpperCase() : t.type.slice(0, 3).toUpperCase()
                          return (
                            <div
                              className={`absolute top-1 bottom-1 z-10 rounded overflow-hidden border border-surface-accent/30 ${onTransitionClick ? 'cursor-pointer hover:border-surface-accent/60' : 'pointer-events-none'}`}
                              style={{ left: overlapLeft, width: overlapW, transition: suppressTransition ? 'none' : 'left 200ms ease-out' }}
                              title={`${t.type} (${t.duration}s) — click to edit`}
                              onClick={(e) => { e.stopPropagation(); onTransitionClick?.(boundary.scene._id) }}
                            >
                              <div className={`absolute inset-0 ${
                                isBlack ? 'bg-linear-to-r from-transparent via-black/60 to-transparent' :
                                isWhite ? 'bg-linear-to-r from-transparent via-white/30 to-transparent' :
                                isWipe || isGL ? 'bg-linear-to-r from-surface-accent/15 to-surface-accent/15' :
                                'bg-linear-to-r from-surface-accent/10 via-surface-accent/20 to-surface-accent/10'
                              }`} />
                              {!isBlack && !isWhite && (
                                <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'repeating-linear-gradient(135deg, transparent, transparent 3px, currentColor 3px, currentColor 4px)', color: 'var(--surface-accent)' }} />
                              )}
                              <div className="absolute inset-0 flex items-center justify-center">
                                <span className="text-[7px] font-mono text-surface-accent/80 bg-black/40 px-1 rounded">{label} {t.duration}s</span>
                              </div>
                            </div>
                          )
                        })()}
                        {/* Add transition button between clips (when no transition) */}
                        {!hasTransition && !isDraggedForReorder && index > 0 && onTransitionClick && (
                          <div
                            className="absolute z-10 flex items-center justify-center cursor-pointer group/addtrans"
                            style={{ left: left + reorderOffset - 6, top: 2, bottom: 2, width: 14, transition: suppressTransition ? 'none' : 'left 200ms ease-out' }}
                            onClick={(e) => { e.stopPropagation(); onTransitionClick(boundary.scene._id) }}
                            title="Add transition"
                          >
                            <div className="w-4 h-4 rounded-full bg-surface-border border border-surface-muted flex items-center justify-center opacity-0 group-hover/addtrans:opacity-100 hover:bg-surface-elevated hover:border-surface-foreground transition-opacity">
                              <svg width="8" height="8" viewBox="0 0 10 10" fill="none"><path d="M5 2V8M2 5H8" stroke="var(--surface-muted)" strokeWidth="1.5" strokeLinecap="round" /></svg>
                            </div>
                          </div>
                        )}
                        <div
                          data-clip-status={boundary.scene.videoStatus}
                          data-clip-title={boundary.scene.title || `Clip ${index + 1}`}
                          className={`absolute top-1 bottom-1 rounded overflow-hidden border group/clip ${isDraggedForReorder ? 'opacity-30' : 'cursor-grab'} ${hasContinuity && !boundary.scene.linkGroupId ? 'border-l-teal-400/70' : ''} ${selectedItems.has('clip:' + boundary.scene._id) ? 'border-sky-500 ring-2 ring-sky-400/40' : isActive ? 'border-sky-400/70 ring-1 ring-sky-300/30' : 'border-surface-border/80'}`}
                          style={{
                            left: left + reorderOffset + 2,
                            width: Math.max(width - 4, 8),
                            borderLeftWidth: boundary.scene.linkGroupId ? 3 : undefined,
                            borderLeftColor: boundary.scene.linkGroupId ? linkGroupColor(boundary.scene.linkGroupId) : undefined,
                            transition: (clipDrag && clipDrag.started && clipDrag.clipIndex === index) || committedClipTrim?.clipId === boundary.scene._id || (pendingReorder && !pendingReorder.settled && index === pendingReorder.dragIndex) || suppressTransition ? 'none' : 'left 200ms ease-out, width 200ms ease-out',
                          }}
                          onClick={(e) => { if (!clipDragRef.current) { selectItem('clip:' + boundary.scene._id, e) } }}
                          onMouseDown={(e) => {
                            if (e.button !== 0) return
                            const target = e.target as HTMLElement
                            if (target.dataset.trimHandle) return
                            const clickTime = getTimeFromMouseX(e.clientX)
                            committedTimeRef.current = clickTime
                            isHoveringRef.current = false
                            onSeek(clickTime)
                            if (activeTimelineMode === 'preserve_positions') return
                            handleClipDragStart(e, 'reorder', boundary.scene._id, index)
                          }}
                        >
                          {isSolidColor ? (
                            <div className="w-full h-full" style={{ backgroundColor: boundary.scene.solidColor }} />
                          ) : thumbnailUrl ? (
                            <div className="w-full h-full flex overflow-hidden">
                              {Array.from({ length: Math.max(1, Math.ceil((width - 4) / 64)) }).map((_, i) => (
                                <img key={i} src={thumbnailUrl} alt="" className="h-full w-16 object-cover shrink-0" draggable={false} />
                              ))}
                            </div>
                          ) : (
                            <div className="w-full h-full bg-surface-elevated flex items-center justify-center">
                              <Hourglass size={12} className="text-surface-muted" />
                            </div>
                          )}
                          {/* Fade edge gradients */}
                          {boundary.scene.effects?.fadeIn && (() => {
                            const fadePct = Math.min(100, (boundary.scene.effects!.fadeIn!.duration / effDur) * 100)
                            const color = boundary.scene.effects!.fadeIn!.color === 'white' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                            return <div className="absolute top-0 bottom-0 left-0" style={{ width: `${fadePct}%`, background: `linear-gradient(to right, ${color}, transparent)` }} />
                          })()}
                          {boundary.scene.effects?.fadeOut && (() => {
                            const fadePct = Math.min(100, (boundary.scene.effects!.fadeOut!.duration / effDur) * 100)
                            const color = boundary.scene.effects!.fadeOut!.color === 'white' ? 'rgba(255,255,255,0.85)' : 'rgba(0,0,0,0.85)'
                            return <div className="absolute top-0 bottom-0 right-0" style={{ width: `${fadePct}%`, background: `linear-gradient(to left, ${color}, transparent)` }} />
                          })()}
                          {isGenerating && (
                            <div className="absolute inset-0 bg-black/10 flex items-center justify-center">
                              <Loader2 size={14} className="animate-spin text-surface-muted" />
                            </div>
                          )}
                          {isStale && hasVideo && (
                            <div className="absolute top-0 left-0 right-0 h-0.5 bg-surface-muted" />
                          )}
                          {/* Letterbox indicator */}
                          {boundary.scene.effects?.letterbox && (
                            <>
                              <div className="absolute top-0 left-0 right-0 h-1.5 bg-black/70" />
                              <div className="absolute bottom-0 left-0 right-0 h-1.5 bg-black/70" />
                            </>
                          )}
                          <div
                            className="absolute top-0 left-0 bg-black/50 text-white text-[8px] px-1 py-px font-mono leading-tight rounded-br"
                            title={rawDiffersFromEffective ? `Raw: ${boundary.scene.duration}s → Effective: ${effDur.toFixed(1)}s` : undefined}
                          >
                            {index + 1}
                          </div>
                          {/* Speed badge */}
                          {hasSpeed && (
                            <span className="absolute top-0 right-0 bg-orange-500/90 text-white text-[7px] px-0.5 py-px font-mono leading-tight rounded-bl flex items-center gap-px" title={`Speed: ${boundary.scene.speed}x`}>
                              <Gauge size={7} />
                              {boundary.scene.speed}x
                            </span>
                          )}
                          {/* Effects dots */}
                          {hasEffects && (
                            <div className="absolute top-0.5 left-5 flex items-center gap-px pointer-events-none">
                              {boundary.scene.effects?.colorGrade && (
                                <span className={`w-1.5 h-1.5 rounded-full ${
                                  boundary.scene.effects.colorGrade === 'warm' ? 'bg-amber-400' :
                                  boundary.scene.effects.colorGrade === 'cool' ? 'bg-sky-400' :
                                  boundary.scene.effects.colorGrade === 'desaturated' ? 'bg-surface-muted' :
                                  boundary.scene.effects.colorGrade === 'high_contrast' ? 'bg-white border border-black/50' :
                                  'bg-purple-400'
                                }`} title={`Color: ${boundary.scene.effects.colorGrade}`} />
                              )}
                              {boundary.scene.effects?.vignette && <span className="w-1.5 h-1.5 rounded-full bg-surface-muted" title="Vignette" />}
                              {boundary.scene.effects?.bloom != null && boundary.scene.effects.bloom > 0 && <span className="w-1.5 h-1.5 rounded-full bg-yellow-400" title="Bloom" />}
                              {boundary.scene.effects?.vhs && <span className="w-1.5 h-1.5 rounded-full bg-red-500" title="VHS" />}
                              {boundary.scene.effects?.shake != null && boundary.scene.effects.shake > 0 && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" title="Shake" />}
                            </div>
                          )}
                          {boundary.scene.linkGroupId && (
                            <div
                              className="absolute bottom-0.5 left-0.5 pointer-events-none"
                              title={`Link group: ${boundary.scene.linkGroupId}`}
                            >
                              <svg width="10" height="10" viewBox="0 0 16 16" fill="none">
                                <path d="M6.5 10.5L5 12a2.83 2.83 0 0 1-4-4l3-3a2.83 2.83 0 0 1 4 0" stroke={linkGroupColor(boundary.scene.linkGroupId)} strokeWidth="1.5" strokeLinecap="round" />
                                <path d="M9.5 5.5L11 4a2.83 2.83 0 0 1 4 4l-3 3a2.83 2.83 0 0 1-4 0" stroke={linkGroupColor(boundary.scene.linkGroupId)} strokeWidth="1.5" strokeLinecap="round" />
                              </svg>
                            </div>
                          )}
                          <div className="absolute bottom-0.5 right-0.5">
                            <GenerationBadge scene={boundary.scene} hasContinuity={hasContinuity} />
                          </div>
                          {/* Trim handles */}
                          <div
                            data-trim-handle="left"
                            className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize opacity-0 group-hover/clip:opacity-100 hover:opacity-100! bg-sky-400/60 transition-opacity rounded-l z-10"
                            onMouseDown={(e) => { e.stopPropagation(); handleClipDragStart(e, 'trim-left', boundary.scene._id, index) }}
                          />
                          <div
                            data-trim-handle="right"
                            className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize opacity-0 group-hover/clip:opacity-100 hover:opacity-100! bg-sky-400/60 transition-opacity rounded-r z-10"
                            onMouseDown={(e) => { e.stopPropagation(); handleClipDragStart(e, 'trim-right', boundary.scene._id, index) }}
                          />
                          {/* Trim dimmed overlay during trim drag */}
                          {trimPreview && clipDrag?.type === 'trim-left' && trimPreview.trimStart > 0 && (
                            <div className="absolute top-0 bottom-0 left-0 bg-black/40 pointer-events-none" style={{ width: `${(trimPreview.trimStart / boundary.scene.duration) * 100}%` }} />
                          )}
                          {trimPreview && clipDrag?.type === 'trim-right' && trimPreview.trimEnd > 0 && (
                            <div className="absolute top-0 bottom-0 right-0 bg-black/40 pointer-events-none" style={{ width: `${(trimPreview.trimEnd / boundary.scene.duration) * 100}%` }} />
                          )}
                        </div>
                      </div>
                    )
                  })}
                  {/* Ghost element for reorder drag */}
                  {clipDrag?.type === 'reorder' && clipDrag.started && (() => {
                    const boundary = sceneBoundaries[clipDrag.clipIndex]
                    if (!boundary) return null
                    const dx = clipDrag.currentX - clipDrag.initialX
                    const ghostLeft = clipDrag.initialLeft + dx + 2
                    const ghostWidth = clipDrag.initialWidth - 4
                    const scene = boundary.scene
                    const hasVideo = scene.videoStatus === 'completed' && scene.videoUrl
                    const thumbnailUrl = hasVideo
                      ? (scene.firstFrameUrl || (scene.videoUrl && videoThumbs.get(scene.videoUrl)) || null)
                      : scene.startKeyframeUrl || scene.storyboardImageUrl || scene.firstFrameUrl || null
                    return (
                      <div
                        className="absolute top-1 bottom-1 rounded overflow-hidden border border-sky-400 bg-surface/50 shadow-lg shadow-black/40 z-30 pointer-events-none opacity-60"
                        style={{ left: ghostLeft, width: Math.max(ghostWidth, 8) }}
                      >
                        {scene.solidColor ? (
                          <div className="w-full h-full opacity-80" style={{ backgroundColor: scene.solidColor }} />
                        ) : thumbnailUrl ? (
                          <div className="w-full h-full flex overflow-hidden opacity-80">
                            {Array.from({ length: Math.max(1, Math.ceil(ghostWidth / 64)) }).map((_, i) => (
                              <img key={i} src={thumbnailUrl} alt="" className="h-full w-16 object-cover shrink-0" draggable={false} />
                            ))}
                          </div>
                        ) : (
                          <div className="w-full h-full bg-surface-elevated opacity-80" />
                        )}
                      </div>
                    )
                  })()}
                  {/* Insert indicator for reorder */}
                  {clipDrag?.type === 'reorder' && clipDrag.started && (() => {
                    const { insertLeft, targetIndex } = getReorderTarget(clipDrag.currentX, clipDrag.clipIndex)
                    const draggedW = durationToWidth(getEffectiveDuration(sceneBoundaries[clipDrag.clipIndex].scene))
                    const indicatorOffset = targetIndex > clipDrag.clipIndex ? -draggedW : 0
                    return (
                      <div
                        className="absolute top-0 bottom-0 z-20 pointer-events-none"
                        style={{ left: insertLeft + indicatorOffset - 1, transition: 'left 200ms ease-out' }}
                      >
                        <div className="absolute inset-y-0.5 w-0.5 bg-sky-400 rounded-full shadow-[0_0_6px_rgba(56,189,248,0.5)]" />
                        <div className="absolute -top-0.5 -left-[3px] w-2 h-2 rounded-full bg-sky-400" />
                        <div className="absolute -bottom-0.5 -left-[3px] w-2 h-2 rounded-full bg-sky-400" />
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>

            {/* Video Audio Track */}
            {hasVideoAudio && (
              <div className="border-b border-surface-border overflow-hidden" style={{ height: collapsedTracks['videoAudio'] ? 16 : VIDEO_AUDIO_TRACK_HEIGHT }}>
                <div
                  className={`relative w-full h-full bg-surface/50 cursor-ns-resize select-none ${mutedTracks['videoAudio'] ? 'opacity-30' : ''}`}
                  onPointerDown={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    ;(e.currentTarget as HTMLElement).setPointerCapture(e.pointerId)
                    gainDragRef.current = true
                    const videoAudioMaster = seekContext?.audioMix.trackVolumes['videoAudio'] ?? 1
                    const rect = e.currentTarget.getBoundingClientRect()
                    const x = e.clientX - rect.left + (scrollContainerRef.current?.scrollLeft ?? 0)
                    const y = e.clientY - rect.top
                    const linear = Math.max(0, Math.min(1, 1 - y / rect.height))
                    const vol = Math.round(linear * linear * 100) / 100
                    for (const b of sceneBoundaries) {
                      if (b.scene.videoStatus !== 'completed') continue
                      const cl = timeToLeft(b.start) + 2
                      const cw = durationToWidth(getEffectiveDuration(b.scene)) - 4
                      if (x >= cl && x <= cl + cw) {
                        setGainDragClipId(b.scene._id)
                        seekContext?.setClipVolume(b.scene._id, videoAudioMaster > 0 ? Math.min(1, vol / videoAudioMaster) : vol)
                        break
                      }
                    }
                  }}
                  onPointerMove={(e) => {
                    if (!gainDragRef.current) return
                    const videoAudioMaster = seekContext?.audioMix.trackVolumes['videoAudio'] ?? 1
                    const rect = e.currentTarget.getBoundingClientRect()
                    const x = e.clientX - rect.left + (scrollContainerRef.current?.scrollLeft ?? 0)
                    const y = e.clientY - rect.top
                    const linear = Math.max(0, Math.min(1, 1 - y / rect.height))
                    const vol = Math.round(linear * linear * 100) / 100
                    for (const b of sceneBoundaries) {
                      if (b.scene.videoStatus !== 'completed') continue
                      const cl = timeToLeft(b.start) + 2
                      const cw = durationToWidth(getEffectiveDuration(b.scene)) - 4
                      if (x >= cl && x <= cl + cw) {
                        if (gainDragClipId !== b.scene._id) setGainDragClipId(b.scene._id)
                        seekContext?.setClipVolume(b.scene._id, videoAudioMaster > 0 ? Math.min(1, vol / videoAudioMaster) : vol)
                        break
                      }
                    }
                  }}
                  onPointerUp={() => {
                    gainDragRef.current = false
                    setGainDragClipId(null)
                  }}
                >
                  {sceneBoundaries.map((boundary, index) => {
                    const s = boundary.scene
                    if (s.videoStatus !== 'completed') return null
                    if (!s.nativeAudioEnabled && !(s.transcription?.words && s.transcription.words.length > 0)) return null
                    const left = timeToLeft(boundary.start)
                    const width = durationToWidth(getEffectiveDuration(boundary.scene))
                    const buckets = Math.max(40, Math.ceil((width - 4) / 2))
                    const audioReorderInfo = (clipDrag?.type === 'reorder' && clipDrag.started)
                      ? { dragIndex: clipDrag.clipIndex, targetIndex: clipDrag.targetIndex }
                      : pendingReorder
                    let audioReorderOffset = 0
                    if (audioReorderInfo && index !== audioReorderInfo.dragIndex) {
                      const dragIdx = audioReorderInfo.dragIndex
                      const tgtIdx = audioReorderInfo.targetIndex
                      const draggedW = durationToWidth(getEffectiveDuration(sceneBoundaries[dragIdx].scene))
                      if (tgtIdx > dragIdx && index > dragIdx && index < tgtIdx) audioReorderOffset = -draggedW
                      else if (tgtIdx < dragIdx && index >= tgtIdx && index < dragIdx) audioReorderOffset = draggedW
                    }
                    const videoAudioMaster = seekContext?.audioMix.trackVolumes['videoAudio'] ?? 1
                    const clipVol = (clipVolumes[boundary.scene._id] ?? 1) * videoAudioMaster
                    const isMuted = !!clipMutes[boundary.scene._id]
                    return (
                      <GainClip
                        key={boundary.scene._id}
                        scene={boundary.scene}
                        numBuckets={buckets}
                        volume={clipVol}
                        muted={isMuted}
                        left={left + audioReorderOffset + 2}
                        width={width - 4}
                        currentTime={currentTime}
                        globalStart={boundary.start}
                        onSeek={onSeek}
                        transition={suppressTransition ? 'none' : 'left 200ms ease-out'}
                        activeClipId={gainDragClipId}
                        onToggleMute={() => toggleClipMute(boundary.scene._id)}
                      />
                    )
                  })}
                </div>
              </div>
            )}

            {/* Audio Tracks */}
            {musicTracks.length > 0 && (
              <AudioTrackContent tracks={musicTracks} totalDuration={effectiveDuration} timelineWidth={timelineWidth} color="bg-sky-900/40" onTrackUpdate={onAudioTrackUpdate} collapsed={!!collapsedTracks['music']} muted={!!mutedTracks['music']} masterVolume={seekContext?.audioMix.trackVolumes['music'] ?? 1} clipVolumes={clipVolumes} onClipVolumeChange={(id, v) => seekContext?.setClipVolume(id, v)} selectedItems={selectedItems} onSelect={selectItem} dark trackHeight={AUDIO_TRACK_HEIGHT} currentTime={currentTime} onSeek={onSeek} onClickSeek={handleClickSeek} onEnvelopeChange={onEnvelopeChange} />
            )}
            {sfxTracks.length > 0 && (
              <AudioTrackContent tracks={sfxTracks} totalDuration={effectiveDuration} timelineWidth={timelineWidth} color="bg-sky-900/40" onTrackUpdate={onAudioTrackUpdate} collapsed={!!collapsedTracks['sfx']} muted={!!mutedTracks['sfx']} masterVolume={seekContext?.audioMix.trackVolumes['sfx'] ?? 1} clipVolumes={clipVolumes} onClipVolumeChange={(id, v) => seekContext?.setClipVolume(id, v)} selectedItems={selectedItems} onSelect={selectItem} dark trackHeight={AUDIO_TRACK_HEIGHT} currentTime={currentTime} onSeek={onSeek} onClickSeek={handleClickSeek} onEnvelopeChange={onEnvelopeChange} />
            )}
            {voiceoverTracks.length > 0 && (
              <AudioTrackContent tracks={voiceoverTracks} totalDuration={effectiveDuration} timelineWidth={timelineWidth} color="bg-sky-900/40" onTrackUpdate={onAudioTrackUpdate} collapsed={!!collapsedTracks['voice']} muted={!!mutedTracks['voice']} masterVolume={seekContext?.audioMix.trackVolumes['voice'] ?? 1} clipVolumes={clipVolumes} onClipVolumeChange={(id, v) => seekContext?.setClipVolume(id, v)} selectedItems={selectedItems} onSelect={selectItem} dark trackHeight={AUDIO_TRACK_HEIGHT} currentTime={currentTime} onSeek={onSeek} onClickSeek={handleClickSeek} onEnvelopeChange={onEnvelopeChange} />
            )}

            {/* Text Overlay Track */}
            {textOverlays.length > 0 && (
              <div className="border-b border-surface-border overflow-hidden" style={{ height: TEXT_OVERLAY_TRACK_HEIGHT }}>
                <div className="relative w-full h-full bg-surface/50">
                  {textOverlays.map((overlay) => {
                    const isDragging = textOverlayDrag?.overlayId === overlay.id
                    const committed = textOverlayCommittedRef.current
                    const hasCommitted = committed && committed.overlayId === overlay.id
                    if (hasCommitted) {
                      const close = (a: number, b: number) => Math.abs(a - b) < 0.05
                      if (close(overlay.startTime, committed.startTime) && close(overlay.duration, committed.duration)) {
                        textOverlayCommittedRef.current = null
                      }
                    }
                    const st = isDragging && textOverlayLocal ? textOverlayLocal.startTime : hasCommitted ? committed.startTime : overlay.startTime
                    const dur = isDragging && textOverlayLocal ? textOverlayLocal.duration : hasCommitted ? committed.duration : overlay.duration
                    const overlayLeft = timeToLeft(st)
                    const overlayWidth = durationToWidth(dur)
                    return (
                      <div
                        key={overlay.id}
                        className={`absolute top-1 bottom-1 rounded-sm border overflow-hidden flex items-center group/overlay ${isDragging ? 'border-violet-500 ring-1 ring-violet-400 shadow-lg z-10' : selectedOverlayId === overlay.id ? 'border-violet-400 ring-1 ring-violet-400/60 bg-violet-800/40 z-10' : 'border-violet-700/40'} ${selectedOverlayId !== overlay.id ? 'bg-violet-900/30' : ''} ${isDragging && textOverlayDrag?.type === 'move' ? 'cursor-grabbing' : 'cursor-grab'}`}
                        style={{ left: overlayLeft + 1, width: Math.max(overlayWidth - 2, 12) }}
                        title={overlay.text}
                        onMouseDown={(e) => {
                          if (e.button !== 0 || (e.target as HTMLElement).dataset.edge) return
                          e.stopPropagation(); e.preventDefault()
                          const clickTime = getTimeFromMouseX(e.clientX)
                          committedTimeRef.current = clickTime
                          isHoveringRef.current = false
                          onSeek(clickTime)
                          textOverlayLocalRef.current = { startTime: overlay.startTime, duration: overlay.duration }
                          setTextOverlayLocal({ startTime: overlay.startTime, duration: overlay.duration })
                          setTextOverlayDrag({ type: 'move', overlayId: overlay.id, initialX: e.clientX, initialStartTime: overlay.startTime, initialDuration: overlay.duration })
                        }}
                      >
                        <div data-edge="left" className="absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-violet-400/20 rounded-l-sm z-10" onMouseDown={(e) => {
                          e.stopPropagation(); e.preventDefault()
                          textOverlayLocalRef.current = { startTime: overlay.startTime, duration: overlay.duration }
                          setTextOverlayLocal({ startTime: overlay.startTime, duration: overlay.duration })
                          setTextOverlayDrag({ type: 'resize-left', overlayId: overlay.id, initialX: e.clientX, initialStartTime: overlay.startTime, initialDuration: overlay.duration })
                        }} />
                        <div data-edge="right" className="absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-violet-400/20 rounded-r-sm z-10" onMouseDown={(e) => {
                          e.stopPropagation(); e.preventDefault()
                          textOverlayLocalRef.current = { startTime: overlay.startTime, duration: overlay.duration }
                          setTextOverlayLocal({ startTime: overlay.startTime, duration: overlay.duration })
                          setTextOverlayDrag({ type: 'resize-right', overlayId: overlay.id, initialX: e.clientX, initialStartTime: overlay.startTime, initialDuration: overlay.duration })
                        }} />
                        <Type size={8} className="text-violet-400 shrink-0 mr-0.5 ml-2" />
                        <span className="text-[7px] text-violet-300 truncate font-medium">{overlay.text}</span>
                        {onRemoveTextOverlay && (
                          <button
                            className="absolute right-0.5 top-0.5 hidden group-hover/overlay:flex items-center justify-center w-3 h-3 rounded-sm bg-surface/80 text-surface-muted hover:text-white hover:bg-red-600/80 z-20"
                            onClick={(e) => { e.stopPropagation(); onRemoveTextOverlay(overlay.id) }}
                            title="Remove text overlay"
                          >
                            <X size={7} />
                          </button>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
            )}


            {/* Export end marker + crosshatch */}
            {(() => {
              const exportEnd = activeExportDuration ?? effectiveDuration
              const markerLeft = timeToLeft(exportEnd)
              const isTrimmed = exportEnd < effectiveDuration - 0.05
              return (
                <>
                  {/* Crosshatch overlay for excluded region */}
                  {isTrimmed && (
                    <div
                      className="absolute top-0 bottom-0 pointer-events-none z-10"
                      style={{
                        left: markerLeft,
                        right: 0,
                        backgroundColor: 'rgba(0,0,0,0.45)',
                        backgroundImage: 'repeating-linear-gradient(-45deg, transparent, transparent 4px, rgba(255,255,255,0.06) 4px, rgba(255,255,255,0.06) 7px)',
                      }}
                    />
                  )}
                  {/* Marker line */}
                  <div className="absolute top-0 bottom-0 z-20" style={{ left: markerLeft }}>
                    <div className={`absolute -left-px top-0 bottom-0 w-px ${isTrimmed ? 'bg-amber-500/40' : 'bg-amber-500/20'}`} />
                    {/* Drag handle */}
                    <div
                      className="absolute -left-[5px] -top-px pointer-events-auto cursor-ew-resize flex flex-col items-center"
                      onMouseDown={handleExportMarkerDown}
                      title={`Export end: ${exportEnd.toFixed(1)}s`}
                    >
                      <div className={`w-[10px] h-[8px] rounded-b-sm flex items-center justify-center ${isTrimmed ? 'bg-amber-500/50 hover:bg-amber-500/70' : 'bg-amber-500/25 hover:bg-amber-500/50'} transition-colors`}>
                        <div className="flex gap-px">
                          <div className="w-px h-1 bg-black/30" />
                          <div className="w-px h-1 bg-black/30" />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              )
            })()}

            {/* Beat markers spanning full timeline height */}
            {timelineBeatMarkers.length > 0 && effectiveDuration > 0 && (
              <div className="absolute inset-0 pointer-events-none z-[1]">
                {timelineBeatMarkers.map((m, i) => {
                  const x = (m.time / effectiveDuration) * timelineWidth
                  if (x < 0 || x > timelineWidth) return null
                  return (
                    <div
                      key={i}
                      className="absolute top-0 bottom-0"
                      style={{
                        left: x,
                        width: m.tier === 'downbeat' ? 1.5 : 1,
                        backgroundColor: m.tier === 'downbeat'
                          ? 'rgba(239, 68, 68, 0.35)'
                          : m.tier === 'strong'
                          ? 'rgba(245, 158, 11, 0.25)'
                          : 'rgba(148, 163, 184, 0.12)',
                      }}
                    />
                  )
                })}
              </div>
            )}

            {/* Committed position ghost line — always rendered, overlaps playhead when not hovering */}
            <div className="absolute top-0 bottom-0 z-10 pointer-events-none" style={{ left: effectiveDuration > 0 ? (committedTimeRef.current / effectiveDuration) * timelineWidth : 0 }}>
              <div className="absolute -left-px top-0 bottom-0 w-0.5 bg-surface-playhead/30" />
            </div>

            {/* Playhead */}
            <div
              ref={playheadRef}
              className="absolute top-0 bottom-0 z-20 pointer-events-none"
              style={{ left: playheadLeft, transition: isPlaying && !isDragging ? 'left 250ms linear' : 'none' }}
            >
              <div className="absolute -left-px top-0 bottom-0 w-0.5 bg-surface-playhead" />
              <div className="absolute -left-[5px] -top-px pointer-events-auto cursor-grab" style={{ width: 0, height: 0, borderLeft: '5px solid transparent', borderRight: '5px solid transparent', borderTop: '6px solid var(--surface-playhead)' }} onMouseDown={handlePlayheadMouseDown} />
            </div>
          </div>
        </div>
      </div>

      {/* Custom Scrollbar */}
      {scrollThumbRatio < 1 && (
        <div className="px-1 py-0.5 bg-surface shrink-0">
          <div
            ref={scrollTrackRef}
            className="relative h-1.5 bg-surface-elevated rounded-full cursor-pointer"
            style={{ marginLeft: TRACK_LABEL_WIDTH }}
            onMouseDown={handleScrollbarMouseDown}
          >
            <div
              className="absolute top-0 bottom-0 bg-surface-muted hover:bg-surface-foreground rounded-full transition-colors"
              style={{
                width: `${scrollThumbRatio * 100}%`,
                left: `${scrollRatio * (1 - scrollThumbRatio) * 100}%`,
              }}
            />
          </div>
        </div>
      )}
      {/* Centered collapse arrow */}
      {onToggleExpand && (
        <div className="relative flex items-center justify-center h-6 bg-gradient-to-r from-purple-950/20 via-purple-800/35 to-purple-950/20 cursor-pointer group/collapse shrink-0" onClick={onToggleExpand} title="Collapse timeline">
          <span className="absolute left-3 text-[9px] font-medium tracking-widest uppercase text-purple-400/40 opacity-0 group-hover/collapse:opacity-100 transition-opacity duration-200">Collapse Timeline</span>
          <ChevronUp size={14} strokeWidth={2} className="text-purple-400/70 group-hover/collapse:text-purple-300" />
        </div>
      )}
    </div>
  )
}



function AudioTrackLabel({ label, icon, collapsed, muted, volume, onToggleCollapse, onToggleMute, onVolumeChange, dark }: {
  label: string; icon: React.ReactNode; collapsed: boolean; muted: boolean; volume?: number
  onToggleCollapse: () => void; onToggleMute: () => void; onVolumeChange?: (v: number) => void; dark?: boolean
}) {
  const gainRef = useRef<HTMLDivElement>(null)
  const draggingRef = useRef(false)
  const [isDragging, setIsDragging] = useState(false)

  const handleGainPointerDown = useCallback((e: React.PointerEvent) => {
    if (!onVolumeChange) return
    e.preventDefault()
    e.stopPropagation()
    ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
    draggingRef.current = true
    setIsDragging(true)
    const rect = gainRef.current!.getBoundingClientRect()
    const linear = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top) / rect.height))
    onVolumeChange(Math.round(linear * linear * 100) / 100)
  }, [onVolumeChange])

  const handleGainPointerMove = useCallback((e: React.PointerEvent) => {
    if (!draggingRef.current || !onVolumeChange) return
    const rect = gainRef.current!.getBoundingClientRect()
    const linear = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top) / rect.height))
    onVolumeChange(Math.round(linear * linear * 100) / 100)
  }, [onVolumeChange])

  const handleGainPointerUp = useCallback(() => {
    draggingRef.current = false
    setIsDragging(false)
  }, [])

  const vol = volume ?? 1
  const linePos = Math.sqrt(vol)

  return (
    <div className="flex h-full group/label">
      <div className="flex flex-col justify-center px-1.5 flex-1 min-w-0">
        <div className="flex items-center gap-1">
          <button onClick={(e) => { e.stopPropagation(); onToggleCollapse() }} className={`shrink-0 ${dark ? 'text-surface-muted hover:text-surface-foreground' : 'text-muted-foreground hover:text-secondary-foreground'}`}>
            {collapsed ? <ChevronRight size={9} /> : <ChevronDown size={9} />}
          </button>
          <span className={dark ? 'text-surface-muted' : 'text-muted-foreground'}>{icon}</span>
          <span className={`text-[9px] truncate ${dark ? 'text-surface-muted' : 'text-muted-foreground'}`}>{label}</span>
          <button
            onClick={(e) => { e.stopPropagation(); onToggleMute() }}
            className={`ml-auto opacity-0 group-hover/label:opacity-100 transition-opacity shrink-0 ${dark ? 'text-surface-muted hover:text-surface-foreground' : 'text-muted-foreground hover:text-secondary-foreground'}`}
          >
            {muted ? <VolumeX size={9} /> : <Volume2 size={9} />}
          </button>
        </div>
      </div>
      {onVolumeChange && !collapsed && (
        <div
          ref={gainRef}
          className={`w-4 shrink-0 relative cursor-ns-resize select-none flex items-center justify-center ${muted ? 'opacity-30' : ''}`}
          onPointerDown={handleGainPointerDown}
          onPointerMove={handleGainPointerMove}
          onPointerUp={handleGainPointerUp}
          title={muted ? 'Muted' : `${Math.round(vol * 100)}%`}
        >
          {/* Track groove */}
          <div className={`absolute left-1/2 -translate-x-1/2 w-[3px] rounded-full top-1 bottom-1 ${dark ? 'bg-white/10' : 'bg-black/10'}`} />
          {/* Fill from bottom */}
          <div
            className="absolute left-1/2 -translate-x-1/2 w-[3px] rounded-full bottom-1"
            style={{
              top: `calc(${(1 - linePos) * 100}% + 4px)`,
              background: dark
                ? 'linear-gradient(to top, rgba(168,85,247,0.7), rgba(168,85,247,0.3))'
                : 'linear-gradient(to top, rgba(168,85,247,0.5), rgba(168,85,247,0.2))',
            }}
          />
          {/* Thumb */}
          <div
            className={`absolute left-1/2 -translate-x-1/2 w-2 h-1 rounded-sm pointer-events-none transition-shadow ${
              isDragging
                ? (dark ? 'bg-purple-400 shadow-[0_0_6px_rgba(168,85,247,0.6)]' : 'bg-purple-500 shadow-[0_0_6px_rgba(168,85,247,0.4)]')
                : (dark ? 'bg-white/70 hover:bg-white' : 'bg-neutral-600 hover:bg-neutral-800')
            }`}
            style={{ top: `calc(${(1 - linePos) * 100}% - 2px)` }}
          />
        </div>
      )}
    </div>
  )
}

const CollapsedTranscriptWords = memo(function CollapsedTranscriptWords({ audioTracks, totalDuration, currentTime, onSeek }: {
  audioTracks: AudioTrack[]; totalDuration: number; currentTime: number; onSeek: (t: number) => void
}) {
  const [measuredRef, setMeasuredRef] = useState<HTMLDivElement | null>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    if (!measuredRef) return
    setWidth(measuredRef.offsetWidth)
    const ro = new ResizeObserver(entries => { setWidth(entries[0]?.contentRect.width ?? 0) })
    ro.observe(measuredRef)
    return () => ro.disconnect()
  }, [measuredRef])

  const allWords = useMemo(() => {
    const result: Array<{ w: string; globalS: number; globalE: number }> = []
    for (const t of audioTracks) {
      if (t.type !== 'voiceover' || !t.transcriptionWords?.length) continue
      const trim = t.trimStart || 0
      for (const word of t.transcriptionWords) {
        if (word.s < trim) continue
        result.push({ w: word.w, globalS: t.startTime + (word.s - trim), globalE: t.startTime + (word.e - trim) })
      }
    }
    result.sort((a, b) => a.globalS - b.globalS)
    return result
  }, [audioTracks])

  const culled = useMemo(() => {
    if (totalDuration <= 0 || width < 40 || allWords.length === 0) return []
    const GAP = 32
    const out: typeof allWords = []
    let lastPx = -GAP
    for (const w of allWords) {
      const px = (w.globalS / totalDuration) * width
      if (px - lastPx >= GAP) { out.push(w); lastPx = px }
    }
    return out
  }, [allWords, totalDuration, width])

  if (allWords.length === 0 || totalDuration <= 0) return null

  return (
    <div ref={setMeasuredRef} className="relative h-[14px] mx-2 overflow-hidden">
      {culled.map((w, i) => {
        const leftPct = (w.globalS / totalDuration) * 100
        const isActive = currentTime >= w.globalS && currentTime <= w.globalE
        const nextPct = i < culled.length - 1 ? (culled[i + 1].globalS / totalDuration) * 100 : 100
        const maxWidthPct = nextPct - leftPct - 0.3
        return (
          <span
            key={i}
            className={`absolute top-0 text-[9px] leading-[14px] truncate select-none cursor-pointer transition-colors ${isActive ? 'text-purple-300 font-medium' : 'text-surface-muted/60 hover:text-surface-foreground'}`}
            style={{ left: `${leftPct}%`, maxWidth: `${Math.max(2, maxWidthPct)}%` }}
            onClick={(e) => { e.stopPropagation(); onSeek(w.globalS) }}
            title={w.w}
          >{w.w}</span>
        )
      })}
    </div>
  )
})

const InlineTranscriptWords = memo(function InlineTranscriptWords({ words, widthPx, currentTime, trimStart = 0, trimEnd = 0, speed = 1, clipDuration, globalStart, onSeek }: {
  words: Array<{ w: string; s: number; e: number }>
  widthPx: number; currentTime: number; trimStart?: number; trimEnd?: number; speed?: number
  clipDuration: number; globalStart: number; onSeek?: (time: number) => void
}) {
  const effectiveDuration = (clipDuration - trimStart - trimEnd) / speed
  const pxPerSec = effectiveDuration > 0 ? widthPx / effectiveDuration : 0
  const clipEnd = clipDuration - trimEnd

  const visibleWords = useMemo(() => {
    if (effectiveDuration <= 0 || !words.length || widthPx < 20) return []
    const result: Array<{ word: string; x: number; w: number; globalStart: number; globalEnd: number; idx: number }> = []
    for (let i = 0; i < words.length; i++) {
      const word = words[i]
      if (word.s < trimStart || word.e > clipEnd) continue
      const localStart = (word.s - trimStart) / speed
      const localEnd = (word.e - trimStart) / speed
      const x = localStart * pxPerSec
      const w = Math.max((localEnd - localStart) * pxPerSec, 2)
      result.push({ word: word.w, x, w, globalStart: globalStart + localStart, globalEnd: globalStart + localEnd, idx: i })
    }
    return result
  }, [words, trimStart, clipEnd, speed, pxPerSec, globalStart, effectiveDuration, widthPx])

  const avgPxPerWord = visibleWords.length > 0 ? widthPx / visibleWords.length : 0

  const MIN_WORD_GAP = 28
  const culled = useMemo(() => {
    if (avgPxPerWord >= MIN_WORD_GAP) return visibleWords
    const result: typeof visibleWords = []
    let lastX = -MIN_WORD_GAP
    for (const v of visibleWords) {
      if (v.x - lastX >= MIN_WORD_GAP) { result.push(v); lastX = v.x }
    }
    return result
  }, [visibleWords, avgPxPerWord])

  if (effectiveDuration <= 0 || !words.length || widthPx < 20) return null

  if (avgPxPerWord < 4) {
    const activeIdx = visibleWords.findIndex(v => currentTime >= v.globalStart && currentTime <= v.globalEnd)
    return (
      <div className="absolute left-0 right-0 bottom-0 h-[6px] pointer-events-none overflow-hidden" style={{ zIndex: 5 }}>
        <div className="absolute inset-0 bg-surface-muted/30 rounded-b-sm" />
        {activeIdx >= 0 && (<div className="absolute top-0 bottom-0 bg-sky-400/50 rounded-sm" style={{ left: visibleWords[activeIdx].x, width: Math.max(visibleWords[activeIdx].w, 3) }} />)}
      </div>
    )
  }

  return (
    <div className="absolute left-0 right-0 bottom-0 h-[16px] pointer-events-auto overflow-hidden" style={{ zIndex: 5 }}>
      <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
      {culled.map((v, ci) => {
        const isActive = currentTime >= v.globalStart && currentTime <= v.globalEnd
        const nextX = ci < culled.length - 1 ? culled[ci + 1].x : widthPx
        const availWidth = nextX - v.x - 3
        if (availWidth < 14) return null
        return (
          <span key={v.idx} className={`absolute top-[1px] text-[9px] leading-[15px] truncate select-none cursor-pointer transition-colors ${isActive ? 'text-sky-300 font-medium' : 'text-surface-muted/70 hover:text-surface-foreground'}`}
            style={{ left: v.x, maxWidth: availWidth }}
            onClick={(e) => { e.stopPropagation(); onSeek?.(v.globalStart) }}
            title={v.word}>{v.word}</span>
        )
      })}
    </div>
  )
})

/** Interactive volume envelope curve — SVG overlay with draggable keyframe points */
function VolumeEnvelope({ envelope, width, height, duration, onChange }: {
  envelope: Array<{ time: number; volume: number }>
  width: number; height: number; duration: number
  onChange: (env: Array<{ time: number; volume: number }>) => void
}) {
  const [dragIdx, setDragIdx] = useState<number | null>(null)
  const [localEnv, setLocalEnv] = useState(envelope)
  const svgRef = useRef<SVGSVGElement>(null)

  // Sync from props when not dragging
  useEffect(() => {
    if (dragIdx === null) setLocalEnv(envelope)
  }, [envelope, dragIdx])

  const toX = (t: number) => (t / duration) * width
  const toY = (v: number) => (1 - Math.sqrt(v)) * height
  const fromX = (px: number) => Math.max(0, Math.min(duration, (px / width) * duration))
  const fromY = (py: number) => {
    const linear = Math.max(0, Math.min(1, 1 - py / height))
    return Math.round(linear * linear * 100) / 100
  }

  // Build filled area path
  const pts = localEnv.map(p => `${toX(p.time)},${toY(p.volume)}`)
  const areaPath = `M0,${height} L${pts.join(' L')} L${width},${height} Z`
  const linePath = `M${pts.join(' L')}`

  const handlePointPointerDown = (e: React.PointerEvent, idx: number) => {
    e.preventDefault()
    e.stopPropagation()
    ;(e.target as SVGElement).setPointerCapture(e.pointerId)
    setDragIdx(idx)
  }

  const handlePointerMove = (e: React.PointerEvent) => {
    if (dragIdx === null || !svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const px = e.clientX - rect.left
    const py = e.clientY - rect.top
    let time = fromX(px)
    const volume = fromY(py)

    // First and last points stay pinned to edges
    if (dragIdx === 0) time = 0
    else if (dragIdx === localEnv.length - 1) time = duration
    else {
      // Constrain between neighbors
      time = Math.max(localEnv[dragIdx - 1].time + 0.01, Math.min(localEnv[dragIdx + 1].time - 0.01, time))
    }
    const updated = [...localEnv]
    updated[dragIdx] = { time, volume }
    setLocalEnv(updated)
  }

  const handlePointerUp = () => {
    if (dragIdx !== null) {
      onChange(localEnv)
      setDragIdx(null)
    }
  }

  const handleDoubleClick = (e: React.MouseEvent) => {
    if (!svgRef.current) return
    const rect = svgRef.current.getBoundingClientRect()
    const px = e.clientX - rect.left
    const py = e.clientY - rect.top
    const time = fromX(px)
    const volume = fromY(py)

    // Find insertion index (keep sorted by time)
    let insertIdx = localEnv.findIndex(p => p.time > time)
    if (insertIdx < 0) insertIdx = localEnv.length
    const updated = [...localEnv]
    updated.splice(insertIdx, 0, { time, volume })
    setLocalEnv(updated)
    onChange(updated)
  }

  const handlePointDoubleClick = (e: React.MouseEvent, idx: number) => {
    e.stopPropagation()
    // Don't delete first/last points
    if (idx === 0 || idx === localEnv.length - 1) return
    const updated = localEnv.filter((_, i) => i !== idx)
    setLocalEnv(updated)
    onChange(updated)
  }

  return (
    <svg
      ref={svgRef}
      className="absolute inset-0 z-10"
      width={width} height={height}
      style={{ cursor: dragIdx !== null ? 'ns-resize' : 'crosshair' }}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onDoubleClick={handleDoubleClick}
    >
      {/* Filled area under curve */}
      <path d={areaPath} fill="rgba(251, 191, 36, 0.12)" />
      {/* Envelope line */}
      <path d={linePath} fill="none" stroke="rgba(251, 191, 36, 0.7)" strokeWidth={1.5} />
      {/* Draggable points */}
      {localEnv.map((p, i) => (
        <circle
          key={i}
          cx={toX(p.time)} cy={toY(p.volume)} r={3.5}
          fill={dragIdx === i ? 'rgb(251, 191, 36)' : 'rgba(251, 191, 36, 0.9)'}
          stroke="rgba(0,0,0,0.4)" strokeWidth={0.5}
          className="cursor-ns-resize hover:r-[5]"
          onPointerDown={(e) => handlePointPointerDown(e, i)}
          onDoubleClick={(e) => handlePointDoubleClick(e, i)}
        />
      ))}
    </svg>
  )
}

function AudioTrackContent({ tracks, totalDuration, timelineWidth, color, onTrackUpdate, collapsed, muted, masterVolume = 1, clipVolumes, onClipVolumeChange, selectedItems, onSelect, dark, trackHeight, currentTime, onSeek, onClickSeek, onEnvelopeChange }: {
  tracks: AudioTrack[]; totalDuration: number; timelineWidth: number; color: string
  onTrackUpdate?: (trackId: string, startTime: number, duration: number, trimStart?: number) => void
  collapsed: boolean; muted: boolean; masterVolume?: number
  clipVolumes?: Record<string, number>; onClipVolumeChange?: (trackId: string, vol: number) => void
  selectedItems: Set<string>; onSelect: (key: string, e: React.MouseEvent) => void
  dark?: boolean; trackHeight?: number; currentTime?: number; onSeek?: (time: number) => void
  onClickSeek?: (clientX: number) => void
  onEnvelopeChange?: (trackId: string, envelope: Array<{ time: number; volume: number }>) => void
}) {
  const h = trackHeight ?? AUDIO_TRACK_HEIGHT
  return (
    <div className={`border-b overflow-hidden ${dark ? 'border-surface-border' : 'border-border'}`} style={{ height: collapsed ? 16 : h }}>
      <div className={`relative w-full h-full ${collapsed ? '' : dark ? 'bg-surface/50' : 'bg-secondary'} ${muted ? 'opacity-30' : ''}`}>
        {tracks.map((track) => (
          <AudioTrackBar key={track.id} track={track} totalDuration={totalDuration} timelineWidth={timelineWidth} color={color} onTrackUpdate={onTrackUpdate} collapsed={collapsed} masterVolume={masterVolume} clipVolume={clipVolumes?.[track.id]} onClipVolumeChange={onClipVolumeChange ? (v) => onClipVolumeChange(track.id, v) : undefined} selected={selectedItems.has('audio:' + track.id)} onSelect={(e) => onSelect('audio:' + track.id, e)} currentTime={currentTime} onSeek={onSeek} onClickSeek={onClickSeek} onEnvelopeChange={onEnvelopeChange ? (env) => onEnvelopeChange(track.id, env) : undefined} />
        ))}
      </div>
    </div>
  )
}

const BeatMarkers = memo(function BeatMarkers({ beatData, trackTrimStart, trackDuration, widthPx }: {
  beatData: BeatData; trackTrimStart: number; trackDuration: number; widthPx: number
}) {
  const markers = useMemo(() => {
    const result: Array<{ x: number; tier: 'downbeat' | 'strong' | 'beat' }> = []
    const downbeatSet = new Set(beatData.downbeats.map(t => Math.round(t * 1000)))
    const strongSet = new Set(beatData.strongHits.map(t => Math.round(t * 1000)))
    // Collect all unique beat times from all tiers
    const allTimes = new Set<number>()
    for (const t of beatData.downbeats) allTimes.add(Math.round(t * 1000))
    for (const t of beatData.strongHits) allTimes.add(Math.round(t * 1000))
    for (const t of beatData.beats) allTimes.add(Math.round(t * 1000))

    const visStart = trackTrimStart
    const visEnd = trackTrimStart + trackDuration

    for (const tMs of allTimes) {
      const tSec = tMs / 1000
      if (tSec < visStart || tSec > visEnd) continue
      const x = ((tSec - visStart) / trackDuration) * widthPx
      if (x < 0 || x > widthPx) continue
      const tier = downbeatSet.has(tMs) ? 'downbeat' : strongSet.has(tMs) ? 'strong' : 'beat'
      result.push({ x, tier })
    }
    return result
  }, [beatData, trackTrimStart, trackDuration, widthPx])

  if (markers.length === 0) return null

  return (
    <div className="absolute inset-0 pointer-events-none">
      {markers.map((m, i) => (
        <div
          key={i}
          className="absolute top-0 bottom-0"
          style={{
            left: m.x,
            width: m.tier === 'downbeat' ? 2 : 1,
            backgroundColor: m.tier === 'downbeat'
              ? 'rgba(239, 68, 68, 0.7)'   // red
              : m.tier === 'strong'
              ? 'rgba(245, 158, 11, 0.5)'   // amber
              : 'rgba(148, 163, 184, 0.25)', // slate
          }}
        />
      ))}
    </div>
  )
})

function AudioTrackBar({ track, totalDuration, timelineWidth, color, onTrackUpdate, collapsed, masterVolume = 1, clipVolume, onClipVolumeChange, selected, onSelect, currentTime, onSeek, onClickSeek, onEnvelopeChange }: {
  track: AudioTrack; totalDuration: number; timelineWidth: number; color: string
  onTrackUpdate?: (trackId: string, startTime: number, duration: number, trimStart?: number) => void
  collapsed: boolean; masterVolume?: number; clipVolume?: number; onClipVolumeChange?: (v: number) => void
  selected: boolean; onSelect: (e: React.MouseEvent) => void; currentTime?: number; onSeek?: (time: number) => void
  onClickSeek?: (clientX: number) => void
  onEnvelopeChange?: (envelope: Array<{ time: number; volume: number }>) => void
}) {
  const initialTrimStart = track.trimStart || 0
  const [dragState, setDragState] = useState<{ type: 'left' | 'right' | 'move' | null; initialX: number; initialStartTime: number; initialDuration: number; initialTrimStart: number }>({ type: null, initialX: 0, initialStartTime: 0, initialDuration: 0, initialTrimStart: 0 })
  const [localStartTime, setLocalStartTime] = useState(track.startTime)
  const [localDuration, setLocalDuration] = useState(track.duration)
  const [localTrimStart, setLocalTrimStart] = useState(initialTrimStart)
  const sourceDurationRef = useRef(initialTrimStart + track.duration)
  const waveform = useWaveformData(track.status === 'ready' ? track.url : undefined)
  const localRef = useRef({ startTime: track.startTime, duration: track.duration, trimStart: initialTrimStart })
  const committedRef = useRef<{ startTime: number; duration: number; trimStart: number } | null>(null)

  useEffect(() => {
    if (dragState.type) return
    const propTrim = track.trimStart || 0
    if (committedRef.current) {
      const close = (a: number, b: number) => Math.abs(a - b) < 0.5
      if (close(track.startTime, committedRef.current.startTime) && close(track.duration, committedRef.current.duration) && close(propTrim, committedRef.current.trimStart)) {
        committedRef.current = null
      } else {
        setLocalStartTime(committedRef.current.startTime)
        setLocalDuration(committedRef.current.duration)
        setLocalTrimStart(committedRef.current.trimStart)
        localRef.current = committedRef.current
        return
      }
    }
    sourceDurationRef.current = Math.max(sourceDurationRef.current, propTrim + track.duration)
    setLocalStartTime(track.startTime)
    setLocalDuration(track.duration)
    setLocalTrimStart(propTrim)
    localRef.current = { startTime: track.startTime, duration: track.duration, trimStart: propTrim }
  }, [track.startTime, track.duration, track.trimStart, dragState.type])

  const displayWaveform = useMemo(() => {
    if (!waveform || waveform.length === 0) return null
    const sourceDuration = Math.max(sourceDurationRef.current, localTrimStart + localDuration, 0.001)
    const startRatio = Math.min(1, Math.max(0, localTrimStart / sourceDuration))
    const endRatio = Math.min(1, Math.max(startRatio, (localTrimStart + localDuration) / sourceDuration))
    const startIdx = Math.min(waveform.length - 1, Math.max(0, Math.floor(startRatio * waveform.length)))
    const endIdx = Math.min(waveform.length, Math.max(startIdx + 1, Math.ceil(endRatio * waveform.length)))
    return waveform.slice(startIdx, endIdx)
  }, [waveform, localTrimStart, localDuration])

  const left = (localStartTime / totalDuration) * timelineWidth
  const width = (localDuration / totalDuration) * timelineWidth

  const handleEdgeMouseDown = (e: React.MouseEvent, type: 'left' | 'right') => {
    e.stopPropagation(); e.preventDefault()
    setDragState({ type, initialX: e.clientX, initialStartTime: localStartTime, initialDuration: localDuration, initialTrimStart: localTrimStart })
  }

  const handleBodyMouseDown = (e: React.MouseEvent) => {
    if ((e.target as HTMLElement).dataset.edge) return
    e.stopPropagation(); e.preventDefault()
    onClickSeek?.(e.clientX)
    setDragState({ type: 'move', initialX: e.clientX, initialStartTime: localStartTime, initialDuration: localDuration, initialTrimStart: localTrimStart })
  }

  useEffect(() => {
    if (!dragState.type) return
    const handleMouseMove = (e: MouseEvent) => {
      const deltaTime = ((e.clientX - dragState.initialX) / timelineWidth) * totalDuration
      if (dragState.type === 'move') {
        const newStart = Math.max(0, Math.min(dragState.initialStartTime + deltaTime, totalDuration - dragState.initialDuration))
        setLocalStartTime(newStart)
        localRef.current.startTime = newStart
      } else if (dragState.type === 'left') {
        const rawDelta = Math.max(-dragState.initialTrimStart, deltaTime)
        const newStart = Math.max(0, dragState.initialStartTime + rawDelta)
        const maxStart = dragState.initialStartTime + dragState.initialDuration - 1
        const clamped = Math.min(newStart, maxStart)
        const actualDelta = clamped - dragState.initialStartTime
        setLocalStartTime(clamped)
        setLocalDuration(dragState.initialDuration - actualDelta)
        setLocalTrimStart(dragState.initialTrimStart + actualDelta)
        localRef.current = { startTime: clamped, duration: dragState.initialDuration - actualDelta, trimStart: dragState.initialTrimStart + actualDelta }
      } else {
        const newDur = Math.max(1, dragState.initialDuration + deltaTime)
        setLocalDuration(newDur)
        sourceDurationRef.current = Math.max(sourceDurationRef.current, localTrimStart + newDur)
        localRef.current.duration = newDur
      }
    }
    const handleMouseUp = () => {
      if (onTrackUpdate && dragState.type) {
        committedRef.current = { startTime: localRef.current.startTime, duration: localRef.current.duration, trimStart: localRef.current.trimStart }
        onTrackUpdate(track.id, localRef.current.startTime, localRef.current.duration, dragState.type === 'left' ? localRef.current.trimStart : undefined)
      }
      setDragState({ type: null, initialX: 0, initialStartTime: 0, initialDuration: 0, initialTrimStart: 0 })
    }
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
    return () => { document.removeEventListener('mousemove', handleMouseMove); document.removeEventListener('mouseup', handleMouseUp) }
  }, [dragState, timelineWidth, totalDuration, track.id, onTrackUpdate])

  const clampedWidth = Math.min(width, timelineWidth - Math.max(0, left))
  const trackVol = clipVolume ?? track.volume
  const effectiveVol = masterVolume * trackVol
  const [gainDragging, setGainDragging] = useState(false)
  const gainDragRef = useRef(false)

  if (collapsed) {
    return (
      <div
        className={`absolute top-0.5 bottom-0.5 rounded-sm overflow-hidden cursor-pointer ${selected ? 'ring-1 ring-sky-400/50' : ''}`}
        style={{ left: Math.max(0, left), width: clampedWidth }}
        onClick={onSelect}
      >
        {displayWaveform ? (
          <WaveformCanvas data={displayWaveform} gain={effectiveVol} />
        ) : (
          <div className="w-full h-full bg-sky-200/40" />
        )}
        <span className="absolute left-1 top-0 bottom-0 flex items-center text-[7px] text-surface-foreground font-medium pointer-events-none">{track.name}</span>
      </div>
    )
  }
  const linePos = Math.sqrt(effectiveVol)
  const gainLineY = `${(1 - linePos) * 100}%`

  return (
    <div
      className={`absolute top-0.5 bottom-0.5 group/abar group/gain overflow-hidden bg-surface-elevated transition-shadow ${selected ? 'ring-2 ring-sky-400/50' : ''} ${gainDragging ? 'cursor-ns-resize' : dragState.type === 'move' ? 'cursor-grabbing' : 'cursor-grab'}`}
      style={{ left: Math.max(0, left), width: clampedWidth }}
      title={`${track.name} (${Math.round(effectiveVol * 100)}%)`}
      onClick={onSelect}
      onMouseDown={handleBodyMouseDown}
    >
      {displayWaveform ? (
        <WaveformCanvas data={displayWaveform} gain={effectiveVol} />
      ) : (
        <div className={`w-full h-full ${color}`} />
      )}
      {/* Beat markers */}
      {track.metadata?.beatData && (
        <BeatMarkers
          beatData={track.metadata.beatData}
          trackTrimStart={localTrimStart}
          trackDuration={localDuration}
          widthPx={clampedWidth}
        />
      )}
      {/* Volume envelope or flat gain line */}
      {track.volumeEnvelope && track.volumeEnvelope.length >= 2 && onEnvelopeChange ? (
        <VolumeEnvelope
          envelope={track.volumeEnvelope}
          width={clampedWidth}
          height={AUDIO_TRACK_HEIGHT - 1}
          duration={localDuration}
          onChange={onEnvelopeChange}
        />
      ) : onClipVolumeChange ? (
        <div
          className="absolute left-0 right-0 cursor-ns-resize z-10"
          style={{ top: `calc(${gainLineY} - 4px)`, height: 9 }}
          onPointerDown={(e) => {
            e.preventDefault()
            e.stopPropagation()
            ;(e.target as HTMLElement).setPointerCapture(e.pointerId)
            gainDragRef.current = true
            setGainDragging(true)
          }}
          onPointerMove={(e) => {
            if (!gainDragRef.current) return
            const barEl = e.currentTarget.parentElement
            if (!barEl) return
            const rect = barEl.getBoundingClientRect()
            const linear = Math.max(0, Math.min(1, 1 - (e.clientY - rect.top) / rect.height))
            const vol = Math.round(linear * linear * 100) / 100
            onClipVolumeChange(masterVolume > 0 ? Math.min(1, vol / masterVolume) : vol)
          }}
          onPointerUp={() => {
            gainDragRef.current = false
            setGainDragging(false)
          }}
          onDoubleClick={(e) => {
            // Double-click flat gain line → create initial envelope
            if (onEnvelopeChange) {
              e.stopPropagation()
              const vol = effectiveVol
              onEnvelopeChange([{ time: 0, volume: vol }, { time: localDuration, volume: vol }])
            }
          }}
        >
          <div className="absolute left-0 right-0 top-1/2 -translate-y-px">
            <GainLine active={gainDragging} />
          </div>
        </div>
      ) : (
        <div className="absolute left-0 right-0 pointer-events-none" style={{ top: gainLineY }}>
          <GainLine active={false} />
        </div>
      )}
      {gainDragging && (
        <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-black/70 text-surface-accent text-[9px] font-mono px-1.5 py-0.5 rounded pointer-events-none z-20">
          {Math.round(effectiveVol * 100)}%
        </div>
      )}
      <div data-edge="left" className={`absolute left-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-black/10 rounded-l-sm ${dragState.type === 'left' ? 'bg-black/20' : ''}`} onMouseDown={(e) => handleEdgeMouseDown(e, 'left')} />
      <div data-edge="right" className={`absolute right-0 top-0 bottom-0 w-1.5 cursor-ew-resize hover:bg-black/10 rounded-r-sm ${dragState.type === 'right' ? 'bg-black/20' : ''}`} onMouseDown={(e) => handleEdgeMouseDown(e, 'right')} />
      <span className="absolute top-0 left-1.5 text-[8px] text-surface-foreground font-medium pointer-events-none">{track.name}</span>
      {track.transcriptionWords && track.transcriptionWords.length > 0 && currentTime !== undefined && (
        <InlineTranscriptWords
          words={track.transcriptionWords}
          widthPx={clampedWidth}
          currentTime={currentTime}
          trimStart={localTrimStart}
          clipDuration={localTrimStart + localDuration}
          globalStart={localStartTime}
          onSeek={onSeek}
        />
      )}
    </div>
  )
}
